import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  FileText,
  Users,
  Receipt,
  Settings,
  Trash2,
  Info,
  Download,
  Send,
  ShieldCheck,
  Calendar,
  CheckCircle2,
  UserPlus,
  Save,
  Loader2,
  Wifi,
  WifiOff,
  TestTube,
  Upload,
  Printer,
  RefreshCw,
  AlertTriangle,
  Edit,
  CalendarDays,
  Eye
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import ComuneAutocomplete from "../components/ComuneAutocomplete";
import StatoAutocomplete from "../components/StatoAutocomplete";
import {
  CODICE_STATO_ITALIA,
  etichettaStatoAlloggiati,
  isCodiceIstatStato,
  isNatoAllEstero,
  normalizzaCodiceStatoAlloggiati,
} from "../utils/alloggiatiCodici";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** gg/mm/aaaa nel fuso Europe/Rome (0 = oggi, 1 = ieri). */
function dataArrivoItaliaRome(giorniFa = 0) {
  const target = new Date(Date.now() - giorniFa * 24 * 60 * 60 * 1000);
  return target.toLocaleDateString("it-IT", {
    timeZone: "Europe/Rome",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** gg/mm/aaaa per il tracciato Alloggiati (da campo `arrivo` in tabella o DB). */
function arrivoToDataArrivoSchedina(arrivo) {
  const oggi = dataArrivoItaliaRome(0);
  if (!arrivo) return oggi;

  let s = String(arrivo).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.slice(0, 10).split("-");
    return `${d}/${m}/${y}`;
  }
  // toLocaleString it-IT: "03/06/2026, 10:17:03" — la virgola va rimossa prima dello split su spazio
  s = s.split(",")[0].trim().split(" ")[0].trim();
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) {
    return `${m[1].padStart(2, "0")}/${m[2].padStart(2, "0")}/${m[3]}`;
  }
  return oggi;
}

/**
 * Data inviata al portale: solo oggi o ieri (regola Alloggiati Web).
 * Reinvii e schedine con data storica usano il giorno corrente (come il job automatico).
 */
function dataArrivoPerPortale(ospite, { forceOggi = false } = {}) {
  const oggi = dataArrivoItaliaRome(0);
  const ieri = dataArrivoItaliaRome(1);
  if (forceOggi || ospite.stato === "DA_REINVIARE") {
    return oggi;
  }
  const parsed = arrivoToDataArrivoSchedina(ospite.arrivo);
  if (parsed === oggi || parsed === ieri) {
    return parsed;
  }
  return oggi;
}

function dataNascitaPerSchedina(raw) {
  if (!raw) return "";
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.slice(0, 10).split("-");
    return `${d}/${m}/${y}`;
  }
  return s.split("T")[0].split(" ")[0];
}

function parseGiorniPermanenza(permanenza) {
  const n = parseInt(String(permanenza || "").replace(/\D/g, ""), 10);
  if (Number.isNaN(n) || n < 1) return 30;
  return Math.min(30, n);
}

/** Valore da salvare in `arrivo` (gg/mm/aaaa, HH:MM:SS) rispettando scelta oggi/ieri. */
function formatArrivoOspite(dataArrivoChoice) {
  const datePart =
    dataArrivoChoice === "ieri" ? dataArrivoItaliaRome(1) : dataArrivoItaliaRome(0);
  const timePart = new Date().toLocaleTimeString("it-IT", {
    timeZone: "Europe/Rome",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  return `${datePart}, ${timePart}`;
}

const INVIO_ERROR_DIALOG_INIT = {
  open: false,
  title: "",
  summary: "",
  dettagli: [],
  erroriOspiti: [],
  rawResponse: null,
};

/** Interpreta errore portale / locale in messaggio e campi da correggere. */
function interpretaErrorePortale(erroreDes, erroreDettaglio, erroreCod) {
  const des = (erroreDes || "").trim();
  const det = (erroreDettaglio || "").trim();
  const cod = (erroreCod || "").trim();
  const blob = `${des} ${det} ${cod}`.toUpperCase();

  let tipo = "PORTALE";
  let titolo = des || "Errore sul portale Alloggiati";
  let messaggio = det || des || "Schedina rifiutata dal portale.";
  let suggerimento =
    "Usa «Correggi dati» per aggiornare i campi segnalati, poi ripeti test o invio.";
  let campiCorreggere = [];
  let puoCorreggere = true;

  if (cod === "LOCAL_VALIDATION" || des === "SCHEDINA_CAMPO_NON_CORRETTO") {
    tipo = cod === "LOCAL_VALIDATION" ? "VALIDAZIONE" : "PORTALE";
    titolo = des === "SCHEDINA_CAMPO_NON_CORRETTO" ? "Campo schedina non corretto" : titolo;
  }

  if (blob.includes("ARRIVO")) {
    titolo = "Data di arrivo";
    messaggio = det || des || "Data di arrivo errata o non ammessa.";
    suggerimento =
      "Il portale accetta solo la data di oggi o di ieri. All'invio da Affidya viene impostata automaticamente; se l'errore persiste controlla credenziali o riprova più tardi.";
    campiCorreggere = [];
    puoCorreggere = false;
  } else if (blob.includes("RILASCIO") || blob.includes("LUOGO")) {
    titolo = "Luogo di rilascio documento";
    messaggio = det || des;
    suggerimento = "Seleziona il comune di rilascio dall'elenco autocomplete (codice a 9 cifre).";
    campiCorreggere = ["luogoRilascio"];
  } else if (blob.includes("COMUNE") && blob.includes("NASCIT")) {
    titolo = "Comune di nascita";
    messaggio = det || des;
    suggerimento =
      "Per l'Italia scegli il comune dall'elenco; per nato all'estero seleziona lo stato di nascita (comune 000000000, provincia EE).";
    campiCorreggere = ["statoNascita", "comuneNascita", "provinciaNascita"];
  } else if (blob.includes("PROVINCIA") || blob.includes(" EE")) {
    titolo = "Provincia di nascita";
    messaggio = det || des;
    suggerimento = "Sigla a 2 lettere (es. RM) oppure EE se nato all'estero.";
    campiCorreggere = ["provinciaNascita", "statoNascita"];
  } else if (blob.includes("NASCIT") && blob.includes("DATA")) {
    titolo = "Data di nascita";
    messaggio = det || des;
    suggerimento = "Inserisci la data in formato gg/mm/aaaa.";
    campiCorreggere = ["dataNascita"];
  } else if (blob.includes("DOCUMENTO") || blob.includes("IDENT")) {
    titolo = "Documento di identità";
    messaggio = det || des;
    suggerimento = "Verifica tipo e numero documento (senza spazi superflui).";
    campiCorreggere = ["numeroDoc", "documento"];
  } else if (blob.includes("CITTADINANZA")) {
    titolo = "Cittadinanza";
    messaggio = det || des;
    suggerimento =
      "Seleziona la cittadinanza dall'elenco stati (codice ISTAT a 9 cifre). Per l'Italia è 100000100, non il testo «ITALIANA».";
    campiCorreggere = ["cittadinanza"];
  } else if (blob.includes("STATO")) {
    titolo = "Stato di nascita";
    messaggio = det || des;
    suggerimento = "Seleziona lo stato di nascita dall'elenco con codice ISTAT.";
    campiCorreggere = ["statoNascita"];
  } else if (blob.includes("COGNOME") || blob.includes("NOME")) {
    titolo = "Anagrafica";
    messaggio = det || des;
    suggerimento = "Controlla cognome e nome (solo lettere, come sul documento).";
    campiCorreggere = ["cognome", "nome"];
    puoCorreggere = false;
  }

  if (blob.includes("TOKEN") || blob.includes("AUTENTIC") || blob.includes("CREDENZ")) {
    tipo = "CONFIGURAZIONE";
    titolo = "Autenticazione";
    messaggio = det || des || "Problema di accesso al portale.";
    suggerimento = "Apri la scheda Configura e verifica utente, password e WSKey.";
    campiCorreggere = [];
    puoCorreggere = false;
  }

  return {
    tipo,
    titolo,
    messaggio,
    dettaglio: det,
    codice: cod || des,
    suggerimento,
    campiCorreggere,
    puoCorreggere,
  };
}

/** Errori per ospite dalla risposta portale (ordine allineato a schedine inviate). */
function buildErroriDaRispostaPortale(schedineToSend, schedineRisultati, dettagliErr) {
  const items = [];

  if (schedineRisultati.length === schedineToSend.length && schedineRisultati.length > 0) {
    schedineRisultati.forEach((res, idx) => {
      if (res?.ok) return;
      items.push({
        ospite: schedineToSend[idx],
        indice: idx + 1,
        ...interpretaErrorePortale(res.errore_des, res.errore_dettaglio, res.errore_cod),
      });
    });
    return items;
  }

  (dettagliErr || []).forEach((riga, i) => {
    const match = String(riga).match(/^Schedina\s*#(\d+)\s*:\s*(.+)$/i);
    const idx = match ? parseInt(match[1], 10) - 1 : i;
    const testo = match ? match[2] : riga;
    const parti = String(testo).split(" — ");
    const des = parti[0] || "";
    const det = parti.slice(1).join(" — ") || "";
    items.push({
      ospite: schedineToSend[idx] || null,
      indice: match ? idx + 1 : i + 1,
      ...interpretaErrorePortale(des, det, ""),
      messaggio: testo,
    });
  });

  return items;
}

/** Errori da validazione locale pre-invio. */
function buildErroriDaValidazioneLocale(schedineConProblemi) {
  return schedineConProblemi.map(({ ospite, problemi }) => ({
    ospite,
    indice: null,
    tipo: "VALIDAZIONE",
    titolo: "Dati mancanti o non validi",
    messaggio: problemi.map((p) => p.label).join(" · "),
    dettaglio: "",
    codice: "VALIDAZIONE_LOCALE",
    suggerimento:
      "Completa i campi sotto con l'editor «Correggi dati», poi ripeti test o invio.",
    campiCorreggere: [...new Set(problemi.map((p) => p.campo))],
    puoCorreggere: true,
  }));
}

function nomeOspiteLabel(ospite) {
  if (!ospite) return "Ospite non identificato";
  const n = `${ospite.cognome || ""} ${ospite.nome || ""}`.trim();
  return n || "Ospite senza nome";
}

function tipoErroreBadgeClass(tipo) {
  switch (tipo) {
    case "VALIDAZIONE":
      return "bg-amber-100 text-amber-800 border-amber-200";
    case "CONFIGURAZIONE":
      return "bg-slate-100 text-slate-800 border-slate-200";
    default:
      return "bg-red-100 text-red-800 border-red-200";
  }
}

function tipoErroreLabel(tipo) {
  switch (tipo) {
    case "VALIDAZIONE":
      return "Validazione Affidya";
    case "CONFIGURAZIONE":
      return "Configurazione";
    default:
      return "Portale Questura";
  }
}

export default function AlloggiatiWeb() {
  const { token, user, activeStruttura } = useAuth();
  const strutturaScopeKey = activeStruttura?.id || user?.activeStrutturaId || user?.struttura_id || "";
  const [activeTab, setActiveTab] = useState("inserisci");
  const [ospiti, setOspiti] = useState([]);
  const [ricevute, setRicevute] = useState([]);
  const [loadingOspiti, setLoadingOspiti] = useState(true);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedOspite, setSelectedOspite] = useState(null);
  
  // ID fascicolo esistente quando si arriva dal prefill (evita duplicati)
  const [prefillFascicoloId, setPrefillFascicoloId] = useState(null);

  // State per modifica data prossimo invio
  const [showEditDateModal, setShowEditDateModal] = useState(false);
  const [editingOspite, setEditingOspite] = useState(null);
  const [newProssimoInvio, setNewProssimoInvio] = useState("");

  // State per la correzione dei codici (Luogo rilascio / Comune nascita) di un
  // ospite già salvato. Necessario per recuperare ospiti creati prima
  // dell'introduzione dei codici Alloggiati Web o arrivati da prefill fascicolo
  // senza codice associato.
  const [showFixDatiModal, setShowFixDatiModal] = useState(false);
  const [fixDatiOspite, setFixDatiOspite] = useState(null);
  const [fixDatiForm, setFixDatiForm] = useState({
    luogoRilascio: "",
    luogoRilascioCodice: "",
    comuneNascita: "",
    comuneNascitaCodice: "",
    provinciaNascita: "",
    statoNascita: "",
    statoNascitaCodice: "",
    cittadinanza: "",
    cittadinanzaCodice: "",
    dataNascita: "",
    numeroDoc: "",
  });
  const [savingFixDati, setSavingFixDati] = useState(false);
  
  // State per aggiungi ricevuta manuale
  const [showAddRicevutaModal, setShowAddRicevutaModal] = useState(false);
  const [ricevutaManuale, setRicevutaManuale] = useState({ data: "", numSchedine: "1" });
  const [savingRicevuta, setSavingRicevuta] = useState(false);
  const [downloadingRicevute, setDownloadingRicevute] = useState(false);

  // Dialog selezione mese per stampa lista
  const [showStampaMeseDialog, setShowStampaMeseDialog] = useState(false);
  const [meseStampa, setMeseStampa] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [loadingStampaMese, setLoadingStampaMese] = useState(false);

  // Dialog esito invio Alloggiati Web (sia test che invio reale)
  const [invioErrorDialog, setInvioErrorDialog] = useState(INVIO_ERROR_DIALOG_INIT);
  const [fixDatiCampiEvidenziati, setFixDatiCampiEvidenziati] = useState([]);
  
  // Form state per nuova schedina
  const [formData, setFormData] = useState({
    tipoAlloggiato: "",
    dataArrivo: "",
    giorni: "30",
    cognome: "",
    nome: "",
    sesso: "",
    dataNascita: "",
    giornoNascita: "",
    meseNascita: "",
    annoNascita: "",
    cittadinanza: "",
    cittadinanzaLabel: "",
    statoNascita: "",
    statoNascitaLabel: "",
    comuneNascita: "",
    comuneNascitaCodice: "",
    provinciaNascita: "",
    tipoDocumento: "",
    numeroDocumento: "",
    luogoRilascio: "",
    luogoRilascioCodice: ""
  });

  // Config state
  const [configData, setConfigData] = useState({
    utente: "",
    password: "",
    wskey: ""
  });

  // ============================================================================
  // Validazione codici per invio Alloggiati Web
  // ----------------------------------------------------------------------------
  // Il portale richiede codici numerici a 9 cifre per Luogo di rilascio e Comune
  // di nascita (devono provenire dall'autocomplete dei comuni). Se manca o vale
  // "000000000" il portale risponde "Luogo Rilascio Documento Errato" / "Comune
  // Nascita Errato". Validiamo prima dell'invio per dare un messaggio chiaro
  // all'utente invece di farglielo scoprire dopo aver provato l'invio.
  // ============================================================================
  const isCodiceComuneItalianoValido = (codice) => {
    if (!codice) return false;
    const c = String(codice).trim();
    if (!c) return false;
    if (c === "000000000") return false;
    if (!/^\d{9}$/.test(c)) return false;
    return true;
  };

  /** True se lo stato di nascita non è Italia (codice o etichetta legacy). */
  const natoAllEstero = (o) => isNatoAllEstero(o.statoNascitaCodice, o.statoNascita);

  /** Comune di nascita: italiano (9 cifre diverso da 000000000) oppure estero 000000000. */
  const isComuneNascitaOk = (o) => {
    if (natoAllEstero(o)) {
      const cn = String(o.comuneNascitaCodice || "").trim();
      return cn === "000000000" || isCodiceComuneItalianoValido(cn);
    }
    return isCodiceComuneItalianoValido(o.comuneNascitaCodice);
  };

  const getProblemiSchedinaInvio = (o) => {
    const problemi = [];

    // Campi obbligatori base
    if (!o.cognome || !String(o.cognome).trim()) {
      problemi.push({ campo: "cognome", label: "Cognome mancante" });
    }
    if (!o.nome || !String(o.nome).trim()) {
      problemi.push({ campo: "nome", label: "Nome mancante" });
    }
    if (!o.dataNascita || !String(o.dataNascita).trim()) {
      problemi.push({ campo: "dataNascita", label: "Data di nascita mancante" });
    }
    const sessoVal = String(o.sesso || "").trim();
    if (!["1", "2", "M", "F"].includes(sessoVal)) {
      problemi.push({ campo: "sesso", label: "Sesso non valido (atteso 1/M o 2/F)" });
    }
    if (!o.documento || !String(o.documento).trim()) {
      problemi.push({ campo: "documento", label: "Tipo documento mancante" });
    }
    if (!o.numeroDoc || !String(o.numeroDoc).trim()) {
      problemi.push({ campo: "numeroDoc", label: "Numero documento mancante" });
    }

    // Luogo rilascio: sempre comune italiano valido.
    if (!isCodiceComuneItalianoValido(o.luogoRilascioCodice)) {
      problemi.push({ campo: "luogoRilascio", label: "Luogo rilascio documento (codice 9 cifre mancante)" });
    }

    const estero = natoAllEstero(o);
    if (estero) {
      const codStato = String(o.statoNascitaCodice || "").trim();
      if (!codStato || codStato === CODICE_STATO_ITALIA) {
        problemi.push({
          campo: "statoNascita",
          label: "Nato all'estero: apri \"Correggi dati\", seleziona lo Stato di nascita dall'elenco (es. Polonia) per ottenere il codice ISTAT",
        });
      }
      if (!isComuneNascitaOk(o)) {
        problemi.push({
          campo: "comuneNascita",
          label: "Nato all'estero: comune di nascita deve essere 000000000 (si imposta automaticamente scegliendo lo stato non italiano nel dialog)",
        });
      }
      const prov = String(o.provinciaNascita || "").trim().toUpperCase();
      if (prov !== "EE") {
        problemi.push({
          campo: "provinciaNascita",
          label: "Nato all'estero: provincia di nascita deve essere EE",
        });
      }
    } else {
      if (!isCodiceComuneItalianoValido(o.comuneNascitaCodice)) {
        problemi.push({ campo: "comuneNascita", label: "Comune di nascita (codice 9 cifre mancante)" });
      }
      const prov = String(o.provinciaNascita || "").trim();
      if (!prov || prov.length !== 2) {
        problemi.push({
          campo: "provinciaNascita",
          label: "Provincia di nascita mancante o non valida (sigla 2 lettere, es. RM)",
        });
      }
    }

    const cit = normalizzaCodiceStatoAlloggiati(o.cittadinanzaCodice, o.cittadinanza);
    if (!isCodiceIstatStato(cit)) {
      problemi.push({
        campo: "cittadinanza",
        label: "Cittadinanza: seleziona lo stato dall'elenco (per Italia il codice è 100000100, non il testo ITALIANA)",
      });
    }

    return problemi;
  };

  const apriDialogErroreInvio = ({
    title,
    summary,
    schedineToSend = [],
    schedineRisultati = [],
    dettagliErr = [],
    erroriOspiti = null,
    rawResponse = null,
  }) => {
    const errori =
      erroriOspiti ||
      buildErroriDaRispostaPortale(schedineToSend, schedineRisultati, dettagliErr);
    const dettagli =
      dettagliErr.length > 0
        ? dettagliErr
        : errori.map(
            (e) =>
              `${nomeOspiteLabel(e.ospite)}${e.indice ? ` (#${e.indice})` : ""}: ${e.titolo} — ${e.messaggio}`
          );
    setInvioErrorDialog({
      open: true,
      title,
      summary,
      dettagli,
      erroriOspiti: errori,
      rawResponse,
    });
  };

  const wrapCampoFix = (campo, children) =>
    fixDatiCampiEvidenziati.includes(campo) ? (
      <div className="rounded-xl ring-2 ring-amber-400 ring-offset-1 p-3 -m-1 bg-amber-50/60">
        {children}
      </div>
    ) : (
      children
    );

  // Statistiche per le card
  const ospitiInviati = ospiti.filter(o => o.stato === "INVIATA").length;
  const ospitiDaReinviare = ospiti.filter(o => {
    if (!o.data_prossimo_invio) return false;
    return new Date(o.data_prossimo_invio) <= new Date();
  }).length;

  // Lista ospiti ordinata per data prossimo invio (asc), poi per cognome/nome
  const ospitiOrdinati = [...ospiti].sort((a, b) => {
    const dateA = a.data_prossimo_invio ? new Date(a.data_prossimo_invio).getTime() : Infinity;
    const dateB = b.data_prossimo_invio ? new Date(b.data_prossimo_invio).getTime() : Infinity;
    if (dateA !== dateB) return dateA - dateB;
    const cognomeA = (a.cognome || "").toUpperCase();
    const cognomeB = (b.cognome || "").toUpperCase();
    if (cognomeA !== cognomeB) return cognomeA.localeCompare(cognomeB);
    return (a.nome || "").toUpperCase().localeCompare((b.nome || "").toUpperCase());
  });

  // Carica ospiti dal database — esposto come callback per poter essere
  // richiamato anche al cambio tab, al ritorno sulla finestra e dal bottone refresh.
  const fetchOspiti = useCallback(async () => {
    if (!token) return;
    try {
      setLoadingOspiti(true);
      const response = await axios.get(`${API_URL}/api/alloggiati-web/ospiti`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Il campo `prossimoInvio` (stringa locale it-IT) viene scritto dal frontend
      // al momento del salvataggio, ma il job automatico aggiorna solo
      // `data_prossimo_invio` (ISO UTC). Se quest'ultimo è presente, ha la precedenza
      // così il display riflette sempre il dato aggiornato dal backend.
      const ospiti = (response.data.ospiti || []).map(o => {
        if (o.data_prossimo_invio) {
          return {
            ...o,
            prossimoInvio: new Date(o.data_prossimo_invio).toLocaleDateString("it-IT"),
          };
        }
        return o;
      });
      setOspiti(ospiti);
    } catch (error) {
      console.error("Errore caricamento ospiti:", error);
      const saved = localStorage.getItem('alloggiatiWeb_ospiti');
      if (saved) {
        setOspiti(JSON.parse(saved));
      }
    } finally {
      setLoadingOspiti(false);
    }
  }, [token, activeStruttura?.id]);

  // Carica al mount e quando cambia la struttura attiva
  useEffect(() => {
    fetchOspiti();
  }, [fetchOspiti, strutturaScopeKey]);

  // Aggiorna automaticamente ogni 3 minuti e quando l'utente torna sulla pagina
  useEffect(() => {
    const interval = setInterval(fetchOspiti, 3 * 60 * 1000);
    const onVisible = () => { if (document.visibilityState === "visible") fetchOspiti(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [fetchOspiti]);

  // Helper per (ri)caricare lo storico invii dal backend e popolare la tabella ricevute.
  // Usato all'avvio della pagina e dopo ogni azione che modifica lo storico
  // (invio reale, salvataggio invio manuale, download ricevute pendenti, ecc.).
  const loadStoricoInvii = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_URL}/api/alloggiati-web/storico-invii`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const inviiFormattati = (response.data.invii || []).map(invio => {
        const success = invio.result?.success;
        const corrette = invio.result?.schedine_corrette ?? invio.num_schedine_corrette;
        const errate = invio.result?.schedine_errate ?? invio.num_schedine_errate ?? 0;
        // Esito mostrato in tabella: OK solo se davvero non ci sono errori,
        // altrimenti riportiamo il messaggio o un riassunto numerico.
        let esito = "";
        if (invio.test_mode) {
          esito = success && errate === 0 ? 'OK' : (invio.result?.error || (errate > 0 ? `${errate} con errori` : ''));
        } else if (success && errate === 0) {
          esito = 'OK';
        } else if (success && errate > 0) {
          esito = `${corrette ?? 0} ok, ${errate} con errori`;
        } else {
          esito = invio.result?.error || 'Errore';
        }
        return {
          id: invio._id,
          data: invio.created_at ? new Date(invio.created_at).toLocaleDateString('it-IT') : 'N/D',
          dataISO: invio.created_at,
          schedine: invio.num_schedine || invio.schedine_count || 0,
          testMode: invio.test_mode,
          manuale: invio.manuale,
          automatico: invio.automatico,
          ricevutaScaricata: invio.ricevuta_scaricata,
          esito,
        };
      });
      setRicevute(inviiFormattati);
    } catch (error) {
      console.error("Errore caricamento storico invii:", error);
    }
  }, [token, activeStruttura?.id]);

  // Carica ricevute salvate all'avvio e quando cambia struttura
  useEffect(() => {
    if (token) {
      loadStoricoInvii();
    }
  }, [token, strutturaScopeKey, loadStoricoInvii]);

  // Carica configurazione salvata dal backend all'avvio
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/alloggiati-web/config`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.configured) {
          setConfigData({
            utente: response.data.utente || "",
            password: response.data.has_password ? "********" : "",
            wskey: response.data.has_wskey ? "********" : ""
          });
        } else {
          setConfigData({ utente: "", password: "", wskey: "" });
        }
      } catch (error) {
        console.error("Errore caricamento configurazione:", error);
        // Fallback a localStorage per compatibilità
        const savedConfig = localStorage.getItem('alloggiatiWebConfig');
        if (savedConfig) {
          try {
            const parsed = JSON.parse(savedConfig);
            setConfigData(parsed);
          } catch (e) {
            console.error("Errore parsing localStorage:", e);
          }
        }
      }
    };
    
    if (token) {
      fetchConfig();
    }
  }, [token, strutturaScopeKey]);

  // Carica dati precompilati da Fascicolo Assistenziale
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('prefill') === 'true') {
      const prefillData = localStorage.getItem('affidya_alloggiati_prefill');
      if (prefillData) {
        try {
          const data = JSON.parse(prefillData);
          
          // Parsa la data di nascita
          let giornoNascita = "", meseNascita = "", annoNascita = "";
          if (data.dataNascita) {
            const dateParts = data.dataNascita.split("-");
            if (dateParts.length === 3) {
              annoNascita = dateParts[0];
              meseNascita = dateParts[1];
              giornoNascita = dateParts[2];
            }
          }
          
          const statoLabel = etichettaStatoAlloggiati(data.statoNascita, data.statoNascitaLabel);
          const statoCodNorm = normalizzaCodiceStatoAlloggiati(data.statoNascita, data.statoNascitaLabel);
          const natoEsteroPrefill = isNatoAllEstero(statoCodNorm || data.statoNascita, statoLabel);
          const statoCod = statoCodNorm || (natoEsteroPrefill ? "" : CODICE_STATO_ITALIA);

          setFormData(prev => ({
            ...prev,
            cognome: data.cognome || "",
            nome: data.nome || "",
            sesso: data.sesso || "",
            dataNascita: data.dataNascita || "",
            giornoNascita,
            meseNascita,
            annoNascita,
            comuneNascita: natoEsteroPrefill
              ? (statoLabel || data.comuneNascitaLabel || "")
              : (data.comuneNascitaLabel || ""),
            comuneNascitaCodice: natoEsteroPrefill ? "000000000" : (data.comuneNascita || ""),
            provinciaNascita: natoEsteroPrefill ? "EE" : (data.provinciaNascita || ""),
            statoNascita: statoCod,
            statoNascitaLabel: statoLabel || (natoEsteroPrefill ? "" : "ITALIA"),
            cittadinanza: normalizzaCodiceStatoAlloggiati(data.cittadinanza, data.cittadinanzaLabel),
            cittadinanzaLabel: etichettaStatoAlloggiati(data.cittadinanza, data.cittadinanzaLabel),
            tipoDocumento: data.tipoDocumento || "",
            numeroDocumento: data.numeroDocumento || "",
            luogoRilascio: data.luogoRilascioDocumento || "",
          }));

          // Memorizza l'ID del fascicolo di provenienza per evitare duplicati
          if (data.fascicolo_id) {
            setPrefillFascicoloId(data.fascicolo_id);
          }

          // Rimuovi i dati dal localStorage dopo l'uso
          localStorage.removeItem('affidya_alloggiati_prefill');
          
          // Vai al tab inserisci
          setActiveTab("inserisci");
        } catch (e) {
          console.error("Errore caricamento dati precompilati:", e);
        }
      }
    }
  }, []);

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleConfigChange = (field, value) => {
    setConfigData(prev => ({ ...prev, [field]: value }));
  };

  const handleInserisci = async () => {
    // Validazione campi obbligatori secondo manuale Alloggiati Web
    if (!formData.tipoAlloggiato) {
      toast.error("Seleziona il tipo alloggiato");
      return;
    }
    if (!formData.dataArrivo) {
      toast.error("Inserisci la data di arrivo");
      return;
    }
    if (!formData.cognome || !formData.nome) {
      toast.error("Cognome e Nome sono obbligatori");
      return;
    }
    if (!formData.sesso) {
      toast.error("Seleziona il sesso");
      return;
    }
    if (!formData.dataNascita) {
      toast.error("Inserisci la data di nascita");
      return;
    }
    if (!formData.tipoDocumento || !formData.numeroDocumento) {
      toast.error("Documento e numero documento sono obbligatori");
      return;
    }
    if (!isCodiceComuneItalianoValido(formData.luogoRilascioCodice)) {
      toast.error("Seleziona il Luogo di rilascio dall'elenco autocomplete");
      return;
    }
    const italiaCod = CODICE_STATO_ITALIA;
    const natoEsteroForm = formData.statoNascita && formData.statoNascita !== italiaCod;
    if (natoEsteroForm) {
      if (String(formData.provinciaNascita || "").toUpperCase() !== "EE") {
        toast.error("Per nati all'estero la provincia di nascita deve essere EE");
        return;
      }
      const cn = String(formData.comuneNascitaCodice || "").trim();
      if (cn !== "000000000" && !isCodiceComuneItalianoValido(cn)) {
        toast.error("Per nati all'estero seleziona lo Stato di nascita dall'elenco: verrà impostato automaticamente il comune 000000000");
        return;
      }
    } else {
      if (!isCodiceComuneItalianoValido(formData.comuneNascitaCodice)) {
        toast.error("Seleziona il Comune di nascita dall'elenco autocomplete");
        return;
      }
    }
    const citCodice = normalizzaCodiceStatoAlloggiati(formData.cittadinanza, formData.cittadinanzaLabel);
    if (!isCodiceIstatStato(citCodice)) {
      toast.error("Seleziona la cittadinanza dall'elenco");
      return;
    }
    const citLabel = etichettaStatoAlloggiati(citCodice, formData.cittadinanzaLabel);

    // Validazione giorni permanenza (max 30 secondo manuale)
    const giorni = parseInt(formData.giorni);
    if (isNaN(giorni) || giorni < 1 || giorni > 30) {
      toast.error("I giorni di permanenza devono essere tra 1 e 30");
      return;
    }
    
    const nuovoOspite = {
      id: String(Date.now()),
      cognome: formData.cognome.toUpperCase(),
      nome: formData.nome.toUpperCase(),
      dataNascita: formData.dataNascita,
      arrivo: formatArrivoOspite(formData.dataArrivo),
      permanenza: `${formData.giorni} gg`,
      prossimoInvio: new Date(Date.now() + parseInt(formData.giorni) * 24 * 60 * 60 * 1000).toLocaleDateString("it-IT"),
      documento: formData.tipoDocumento || "CARTA DI IDENTITA'",
      numeroDoc: formData.numeroDocumento,
      stato: "DA INVIARE",
      // Dati aggiuntivi per fascicolo assistenziale
      sesso: formData.sesso,
      cittadinanza: citLabel,
      cittadinanzaCodice: citCodice,
      statoNascita: formData.statoNascitaLabel,
      statoNascitaCodice: formData.statoNascita || "",
      comuneNascita: formData.comuneNascita,
      comuneNascitaCodice: formData.comuneNascitaCodice,  // CODICE per Alloggiati Web
      provinciaNascita: formData.provinciaNascita,
      luogoRilascio: formData.luogoRilascio,
      luogoRilascioCodice: formData.luogoRilascioCodice   // CODICE per Alloggiati Web
    };
    
    // Salva ospite nel database
    try {
      const response = await axios.post(`${API_URL}/api/alloggiati-web/ospiti`, nuovoOspite, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const ospiteSalvato = response.data.ospite;
      setOspiti([ospiteSalvato, ...ospiti]);
      
      if (prefillFascicoloId) {
        // Proveniente da un fascicolo esistente: collega l'ospite senza crearne uno nuovo
        try {
          await axios.put(`${API_URL}/api/fascicoli-assistenziali/${prefillFascicoloId}`, {
            sezione: "",
            dati: { ospite_id: ospiteSalvato._id || nuovoOspite.id }
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (error) {
          console.error("Errore aggiornamento ospite_id nel fascicolo:", error);
        }
        setPrefillFascicoloId(null);
        toast.success("Ospite inserito e collegato al fascicolo esistente.");
      } else {
        // Proveniente direttamente da Alloggiati Web: crea fascicolo automaticamente
        try {
          await axios.post(`${API_URL}/api/fascicoli-assistenziali`, {
            ospite_id: ospiteSalvato._id || nuovoOspite.id,
            cognome: nuovoOspite.cognome,
            nome: nuovoOspite.nome,
            dataNascita: formData.dataNascita,
            sesso: formData.sesso === "1" ? "M" : "F",
            codiceFiscale: "",
            comuneNascita: formData.comuneNascita,
            provinciaNascita: formData.provinciaNascita,
            cittadinanza: citCodice,
            cittadinanza_label: citLabel,
            tipoDocumento: formData.tipoDocumento,
            numeroDocumento: formData.numeroDocumento,
            luogoRilascioDocumento: formData.luogoRilascio,
            dataIngresso: new Date().toISOString().split('T')[0],
            createdFrom: "alloggiati_web"
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success("Ospite inserito! Fascicolo assistenziale creato automaticamente.");
        } catch (error) {
          console.error("Errore creazione fascicolo assistenziale:", error);
          toast.success("Ospite inserito! (Fascicolo assistenziale da completare)");
        }
      }
    } catch (error) {
      console.error("Errore salvataggio ospite:", error);
      toast.error("Errore nel salvataggio dell'ospite");
      return;
    }
    
    // Reset form
    setFormData({
      tipoAlloggiato: "",
      dataArrivo: "",
      giorni: "30",
      cognome: "",
      nome: "",
      sesso: "",
      dataNascita: "",
      giornoNascita: "",
      meseNascita: "",
      annoNascita: "",
      cittadinanza: "",
      cittadinanzaLabel: "",
      statoNascita: "",
      statoNascitaLabel: "",
      comuneNascita: "",
      comuneNascitaCodice: "",
      provinciaNascita: "",
      tipoDocumento: "",
      numeroDocumento: "",
      luogoRilascio: "",
      luogoRilascioCodice: ""
    });
    
    setActiveTab("ospiti");
  };

  const handleDelete = (id) => {
    const nuoviOspiti = ospiti.filter(o => o.id !== id);
    setOspiti(nuoviOspiti);
    localStorage.setItem('alloggiatiWeb_ospiti', JSON.stringify(nuoviOspiti));
    toast.success("Ospite eliminato");
  };

  const handleShowDetail = (ospite) => {
    setSelectedOspite(ospite);
    setShowDetailModal(true);
  };

  // State per test connessione
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  
  // State per invio schedine
  const [sendingSchedine, setSendingSchedine] = useState(false);

  const handleSaveConfig = async () => {
    // Validazione
    if (!configData.utente || !configData.password || !configData.wskey) {
      toast.error("Compila tutti i campi di configurazione");
      return;
    }
    
    try {
      // Salva nel backend
      await axios.post(`${API_URL}/api/alloggiati-web/config`, {
        utente: configData.utente,
        password: configData.password,
        wskey: configData.wskey
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Salva anche in localStorage come backup
      localStorage.setItem('alloggiatiWebConfig', JSON.stringify(configData));
      toast.success("Configurazione salvata con successo!");
    } catch (error) {
      console.error("Errore salvataggio config:", error);
      toast.error("Errore nel salvataggio della configurazione");
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus(null);
    
    try {
      const response = await axios.post(`${API_URL}/api/alloggiati-web/test-connection`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setConnectionStatus(response.data);
      if (response.data.success) {
        toast.success("Connessione al portale riuscita!");
      } else {
        toast.error(response.data.message || "Connessione fallita");
      }
    } catch (error) {
      console.error("Errore test connessione:", error);
      setConnectionStatus({ success: false, message: error.response?.data?.detail || "Errore di connessione" });
      toast.error("Errore nel test di connessione");
    } finally {
      setTestingConnection(false);
    }
  };

  // Funzione per scaricare/visualizzare/stampare la ricevuta PDF
  const handleDownloadRicevuta = async (dataInvio, mode = "view") => {
    try {
      toast.info(mode === "print" ? "Preparazione stampa in corso..." : "Caricamento ricevuta in corso...");
      
      // Converti la data nel formato YYYY-MM-DD se necessario
      let dataFormatted = dataInvio;
      if (dataInvio.includes("/")) {
        // Formato dd/mm/yyyy -> YYYY-MM-DD
        const parts = dataInvio.split("/");
        dataFormatted = `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
      
      const response = await axios.get(
        `${API_URL}/api/alloggiati-web/ricevuta/${dataFormatted}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Verifica se la risposta è un errore JSON invece di un PDF
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const error = JSON.parse(text);
        throw new Error(error.detail || "Ricevuta non trovata");
      }
      
      // Crea blob URL
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      if (mode === "print") {
        const iframe = document.createElement("iframe");
        iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:none;";
        iframe.src = url;
        document.body.appendChild(iframe);
        iframe.onload = () => {
          try {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
            toast.success("Dialogo di stampa aperto");
          } catch {
            window.open(url, "_blank");
            toast.warning("Apri la ricevuta e usa Ctrl+P per stampare");
          }
          setTimeout(() => {
            document.body.removeChild(iframe);
            window.URL.revokeObjectURL(url);
          }, 60000);
        };
      } else if (mode === "view") {
        // Apri in nuova finestra per visualizzare
        const newWindow = window.open(url, '_blank');
        if (newWindow) {
          newWindow.document.title = `Ricevuta Alloggiati - ${dataFormatted}`;
          toast.success("Ricevuta aperta");
        } else {
          // Se il popup è bloccato, scarica direttamente
          toast.warning("Popup bloccato - download in corso...");
          downloadBlob(url, `ricevuta_${dataFormatted}.pdf`);
        }
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      } else {
        // Download diretto
        downloadBlob(url, `ricevuta_${dataFormatted}.pdf`);
        toast.success("Ricevuta scaricata!");
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      }
      
    } catch (error) {
      console.error("Errore download ricevuta:", error);

      // Il backend ora può restituire un detail strutturato con
      // { error, error_code, error_des, hints, diagnostica_invio, ... }
      let detail = error.response?.data?.detail;
      // Se il body è un blob (perché responseType = 'blob'), leggilo come testo
      if (detail === undefined && error.response?.data instanceof Blob) {
        try {
          const txt = await error.response.data.text();
          const parsed = JSON.parse(txt);
          detail = parsed?.detail;
        } catch (_) { /* ignora */ }
      }

      let titolo = "Errore nel download della ricevuta";
      const righe = [];

      if (detail && typeof detail === "object") {
        const portalErr = detail.error || "";
        if (portalErr) titolo = portalErr;

        if (detail.data_richiesta) {
          righe.push(`Data richiesta: ${detail.data_richiesta}`);
        }
        if (detail.error_des && detail.error_des !== detail.error) {
          righe.push(`Errore portale: ${detail.error_des}`);
        }
        if (detail.error_code) {
          righe.push(`Codice portale: ${detail.error_code}`);
        }
        if (detail.error_detail && detail.error_detail !== detail.error_des) {
          righe.push(`Dettaglio: ${detail.error_detail}`);
        }
        const diag = detail.diagnostica_invio || {};
        if (
          typeof diag.invii_reali_in_data === "number" ||
          typeof diag.invii_test_in_data === "number"
        ) {
          righe.push(
            `In Affidya per quella data: ${diag.invii_reali_in_data || 0} invii reali, ${diag.invii_test_in_data || 0} test`
          );
        }
        if (Array.isArray(detail.hints) && detail.hints.length) {
          righe.push("");
          righe.push("Possibili cause / cosa controllare:");
          detail.hints.forEach((h) => righe.push(`• ${h}`));
        }
      } else {
        const errorMsg = (typeof detail === "string" ? detail : null) || error.message || "Errore nel download";
        titolo = errorMsg;
        if (/non trovat|not found/i.test(errorMsg)) {
          righe.push("Possibili cause:");
          righe.push("• La data non corrisponde a un invio REALE effettuato");
          righe.push("• Non sono ancora passate 24h dall'invio");
          righe.push("• La data è oltre gli ultimi 30 giorni (le ricevute più vecchie non sono più disponibili)");
          righe.push("• Le credenziali Alloggiati Web appartengono a un'altra struttura");
        }
      }

      // Sonner: il "titolo" è il primo argomento, le righe successive vanno
      // in `description` con whitespace preservato per gli a-capo.
      const description = righe.length > 0 ? righe.join("\n") : undefined;
      toast.error(titolo, {
        description,
        duration: 12000,
        style: { whiteSpace: "pre-line" },
      });
    }
  };
  
  // Helper per download blob
  const downloadBlob = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Funzione per salvare invio manuale nello storico
  const handleSaveInvioManuale = async (scaricaRicevuta = false) => {
    if (!ricevutaManuale.data) {
      toast.error("Inserisci la data dell'invio");
      return;
    }
    
    setSavingRicevuta(true);
    try {
      // Salva l'invio manuale nel database
      const response = await axios.post(
        `${API_URL}/api/alloggiati-web/invio-manuale`,
        {
          data: ricevutaManuale.data,
          num_schedine: parseInt(ricevutaManuale.numSchedine) || 1
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success("Invio salvato nello storico!");

        await loadStoricoInvii();

        // Se richiesto, scarica anche la ricevuta
        if (scaricaRicevuta) {
          handleDownloadRicevuta(ricevutaManuale.data, "view");
        }

        setShowAddRicevutaModal(false);
        setRicevutaManuale({ data: "", numSchedine: "1" });
      }
    } catch (error) {
      console.error("Errore salvataggio invio:", error);
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSavingRicevuta(false);
    }
  };

  const handleForzaDownloadRicevute = async () => {
    setDownloadingRicevute(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/alloggiati-web/forza-download-ricevute`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { scaricate = 0, errori = [] } = response.data;
      if (scaricate > 0) {
        toast.success(`${scaricate} ricevut${scaricate === 1 ? 'a scaricata' : 'e scaricate'} con successo`);
        await loadStoricoInvii();
      } else {
        toast.info("Nessuna ricevuta pendente da scaricare");
      }
      if (errori.length > 0) {
        toast.warning(`${errori.length} ricevut${errori.length === 1 ? 'a' : 'e'} non ancora disponibil${errori.length === 1 ? 'e' : 'i'}`);
      }
    } catch (error) {
      console.error("Errore download ricevute:", error);
      toast.error(error.response?.data?.detail || "Errore nel download delle ricevute");
    } finally {
      setDownloadingRicevute(false);
    }
  };

  // Funzione per eliminare un invio TEST
  const handleDeleteInvioTest = async (invioId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo invio TEST?")) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/api/alloggiati-web/storico-invii/${invioId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Invio TEST eliminato");
      
      // Rimuovi dall'elenco locale
      setRicevute(prev => prev.filter(r => r.id !== invioId));
    } catch (error) {
      console.error("Errore eliminazione invio:", error);
      toast.error(error.response?.data?.detail || "Errore durante l'eliminazione");
    }
  };

  const handleInvioSchedine = async (testMode = true, options = {}) => {
    const { reinvioVerificaIniviati = false } = options;
    const preserveProssimoInvio = reinvioVerificaIniviati === true;

    let schedineToSend;
    if (reinvioVerificaIniviati) {
      schedineToSend = ospiti.filter(
        (o) => o.stato === "INVIATA" && o.attivo !== false
      );
      if (schedineToSend.length === 0) {
        toast.error("Nessun ospite in stato INVIATA da reinviare");
        return;
      }
      if (
        !window.confirm(
          `Verranno inviate al portale Alloggiati Web ${schedineToSend.length} schedin${schedineToSend.length === 1 ? "a" : "e"} ` +
            `già segnate come INVIATA, per verificare che il portale le accetti.\n\n` +
            `La data \"Prossimo invio automatico\" di ciascun ospite non verrà modificata.\n\n` +
            `Per una prova senza effetti usare prima \"Test invio\" sulle stesse schedine (quando disponibile).\n\nContinuare?`
        )
      ) {
        return;
      }
    } else {
      schedineToSend = ospiti.filter(
        (o) => o.stato === "DA INVIARE" || o.stato === "DA_REINVIARE"
      );
    }

    if (schedineToSend.length === 0) {
      toast.error("Nessuna schedina da inviare");
      return;
    }

    const dialogTitle = testMode ? "Esito test invio" : "Esito invio Alloggiati Web";

    // Validazione dei codici PRIMA di chiamare il portale: il portale risponde
    // con messaggi criptici tipo "Luogo Rilascio Documento Errato" quando i
    // codici sono mancanti o non validi. Identifichiamo subito gli ospiti
    // problematici e forniamo all'utente un dialog chiaro con la lista delle
    // schedine incomplete e un suggerimento concreto su come correggerle.
    const schedineConProblemi = schedineToSend
      .map(o => ({ ospite: o, problemi: getProblemiSchedinaInvio(o) }))
      .filter(x => x.problemi.length > 0);

    if (schedineConProblemi.length > 0) {
      const summary =
        `Non posso inviare ${schedineConProblemi.length} schedin${schedineConProblemi.length === 1 ? "a" : "e"}: ` +
        `correggi i dati segnalati per ogni ospite (pulsante sotto).`;
      apriDialogErroreInvio({
        title: testMode ? "Test invio bloccato" : "Invio bloccato",
        summary,
        erroriOspiti: buildErroriDaValidazioneLocale(schedineConProblemi),
      });
      toast.error(
        `Ci sono ${schedineConProblemi.length} schedin${schedineConProblemi.length === 1 ? "a" : "e"} con dati incompleti — correggi dall'elenco errori.`
      );
      return;
    }

    const forceOggiInvio = preserveProssimoInvio;
    const schedineConDataStorica = schedineToSend.filter((o) => {
      const inviata = dataArrivoPerPortale(o, { forceOggi: forceOggiInvio });
      const inDb = arrivoToDataArrivoSchedina(o.arrivo);
      return inDb !== inviata;
    });
    if (schedineConDataStorica.length > 0) {
      toast.info(
        `${schedineConDataStorica.length} schedin${schedineConDataStorica.length === 1 ? "a" : "e"}: ` +
          `data di arrivo impostata a oggi (${dataArrivoItaliaRome(0)}) — il portale accetta solo oggi o ieri.`
      );
    }

    setSendingSchedine(true);

    try {
      // Prepara le schedine nel formato richiesto. L'ordine in `schedineToSend`
      // è lo stesso con cui il backend riceve la richiesta e con cui il
      // portale risponde con `schedine_results` → useremo l'indice per capire
      // quale schedina è stata effettivamente registrata e quale è in errore.
      const schedineFormatted = schedineToSend.map(o => ({
        tipo_alloggiato: "16", // Ospite singolo
        data_arrivo: dataArrivoPerPortale(o, { forceOggi: forceOggiInvio }),
        giorni_permanenza: parseGiorniPermanenza(o.permanenza),
        cognome: o.cognome,
        nome: o.nome,
        sesso: o.sesso || "1",
        data_nascita: dataNascitaPerSchedina(o.dataNascita),
        comune_nascita: natoAllEstero(o)
          ? (String(o.comuneNascitaCodice || "").trim() || "000000000")
          : (o.comuneNascitaCodice || "000000000"),
        provincia_nascita: (o.provinciaNascita || (natoAllEstero(o) ? "EE" : "XX")).toUpperCase().slice(0, 2),
        stato_nascita:
          normalizzaCodiceStatoAlloggiati(o.statoNascitaCodice, o.statoNascita) ||
          CODICE_STATO_ITALIA,
        cittadinanza: normalizzaCodiceStatoAlloggiati(o.cittadinanzaCodice, o.cittadinanza),
        tipo_documento: o.documento?.substring(0, 5) || "IDENT",
        numero_documento: o.numeroDoc,
        luogo_rilascio: o.luogoRilascioCodice || "000000000"
      }));

      const response = await axios.post(`${API_URL}/api/alloggiati-web/invio`, {
        schedine: schedineFormatted,
        test_mode: testMode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = response.data;
      const dettagliErr = Array.isArray(data.dettagli_errori) ? data.dettagli_errori : [];
      const schedineRisultati = Array.isArray(data.schedine_results) ? data.schedine_results : [];
      const schedineCorrette = data.schedine_corrette ?? 0;
      const schedineErrate = data.schedine_errate ?? 0;
      // ID del record alloggiati_invii creato dal backend (presente solo per
      // invii reali/test che hanno generato una riga nello storico).
      const invioIdConfermato = data.invio_id || null;
      // L'invio (o il test) è completamente riuscito se l'esito principale è
      // positivo, almeno una schedina è stata accettata e nessuna in errore.
      const allOk = !!(data.all_ok ?? (data.success && schedineErrate === 0 && schedineCorrette > 0));

      if (testMode) {
        if (allOk) {
          toast.success(
            `Test completato: ${schedineCorrette} schedine corrette, nessun errore.`
          );
        } else {
          const summary = !data.success
            ? data.error || "Il portale ha risposto con esito negativo."
            : `Risultato test: ${schedineCorrette} schedine corrette, ${schedineErrate} con errori.`;
          apriDialogErroreInvio({
            title: dialogTitle,
            summary,
            schedineToSend,
            schedineRisultati,
            dettagliErr,
            rawResponse: data.raw_response || null,
          });
          toast.error(
            !data.success
              ? "Test invio non riuscito — correggi gli ospiti indicati."
              : "Test invio: errori su una o più schedine — correggi gli ospiti indicati."
          );
        }
        return;
      }

      // === Invio REALE alla questura ===

      // 1) Aggiorna lo stato degli ospiti: marca INVIATA solo quelle che il
      //    portale ha accettato. Se non abbiamo `schedine_results` (es. errore
      //    a monte) non tocchiamo lo stato per evitare di marcare per sbaglio
      //    schedine che non sono mai partite.
      let inviateIds = new Set();
      if (schedineRisultati.length === schedineToSend.length) {
        schedineRisultati.forEach((res, idx) => {
          if (res?.ok) inviateIds.add(schedineToSend[idx]._id);
        });
      } else if (allOk) {
        schedineToSend.forEach(o => inviateIds.add(o._id));
      }

      if (inviateIds.size > 0) {
        const updatedOspiti = await Promise.all(ospiti.map(async (o) => {
          if (!inviateIds.has(o._id)) return o;
          try {
            const regBody = {
              ...(invioIdConfermato ? { invio_id: invioIdConfermato } : {}),
              ...(preserveProssimoInvio ? { preserve_prossimo_invio: true } : {}),
            };
            await axios.post(
              `${API_URL}/api/alloggiati-web/ospiti/${o._id}/registra-invio`,
              regBody,
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } catch (err) {
            console.error("Errore registrazione invio:", err);
          }
          const nuovaProssimoISO = preserveProssimoInvio
            ? o.data_prossimo_invio
            : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
          return {
            ...o,
            stato: "INVIATA",
            ultimo_invio_id: invioIdConfermato,
            data_ultimo_invio: new Date().toISOString(),
            data_prossimo_invio: nuovaProssimoISO,
            prossimoInvio: nuovaProssimoISO
              ? new Date(nuovaProssimoISO).toLocaleDateString("it-IT")
              : o.prossimoInvio,
            ...(preserveProssimoInvio
              ? { ultimo_reinvio_verifica_at: new Date().toISOString() }
              : {}),
          };
        }));
        setOspiti(updatedOspiti);
      }

      // 2) Ricarica lo storico ricevute dal backend, così la tabella mostra
      //    solo ed esclusivamente gli invii effettivamente registrati (il
      //    backend non inserisce più una riga se 0 schedine sono partite).
      await loadStoricoInvii();

      // 3) Mostra un messaggio chiaro all'utente
      if (allOk) {
        toast.success(
          preserveProssimoInvio
            ? `Reinvio verifica: ${schedineCorrette} schedin${schedineCorrette === 1 ? "a" : "e"} accettat${schedineCorrette === 1 ? "a" : "e"} dal portale. Le date \"prossimo invio\" non sono state modificate. Controlla ultimo_invio_id nello storico.`
            : `Invio completato: ${schedineCorrette} schedine inviate alla questura.`
        );
      } else if (data.success && schedineCorrette > 0 && schedineErrate > 0) {
        // Successo parziale
        const summary = preserveProssimoInvio
          ? `Reinvio verifica parziale: ${schedineCorrette} accettate, ${schedineErrate} con errori. Le accettate sono tracciate su ultimo_invio_id; correggi le altre e riprova.`
          : `Invio parziale: ${schedineCorrette} schedine inviate correttamente, ${schedineErrate} con errori.\nLe schedine in errore restano "DA INVIARE" — correggi i dati e riprova solo per quelle.`;
        apriDialogErroreInvio({
          title: dialogTitle,
          summary,
          schedineToSend,
          schedineRisultati,
          dettagliErr,
          rawResponse: data.raw_response || null,
        });
        toast.warning(
          preserveProssimoInvio
            ? `Reinvio parziale: ${schedineCorrette}/${schedineToSend.length} — correggi gli ospiti in errore.`
            : `Invio parziale: ${schedineCorrette}/${schedineToSend.length} inviate — correggi gli ospiti in errore.`
        );
      } else {
        // Nessuna schedina inviata: errore globale (token, formato…) oppure
        // tutte le schedine rifiutate dal portale.
        const summary = !data.success
          ? (data.error || "Il portale ha risposto con esito negativo. Nessuna schedina è stata inviata.")
          : `Nessuna schedina è stata inviata. ${schedineErrate} schedin${schedineErrate === 1 ? "a" : "e"} risultan${schedineErrate === 1 ? "o" : "o"} in errore.`;
        apriDialogErroreInvio({
          title: dialogTitle,
          summary,
          schedineToSend,
          schedineRisultati,
          dettagliErr,
          rawResponse: data.raw_response || null,
        });
        toast.error("Invio non riuscito: nessuna schedina inviata — correggi gli ospiti indicati.");
      }
    } catch (error) {
      console.error("Errore invio schedine:", error);
      const detail =
        error.response?.data?.detail || error.message || "Errore nell'invio delle schedine";
      apriDialogErroreInvio({
        title: dialogTitle,
        summary: detail,
        erroriOspiti: [
          {
            ospite: null,
            indice: null,
            tipo: "CONFIGURAZIONE",
            titolo: "Errore di comunicazione",
            messaggio: detail,
            dettaglio: "",
            codice: "",
            suggerimento: "Verifica connessione e configurazione Alloggiati Web, poi riprova.",
            campiCorreggere: [],
            puoCorreggere: false,
          },
        ],
      });
      toast.error(
        testMode
          ? "Test invio non riuscito — vedi i dettagli."
          : "Invio non riuscito — vedi i dettagli."
      );
    } finally {
      setSendingSchedine(false);
    }
  };
  
  // Funzione per stampare la lista ospiti
  const handleStampaLista = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error("Popup bloccato - abilita i popup per stampare");
      return;
    }
    
    const ospitiAttivi = ospiti.filter(o => o.stato !== "ELIMINATO");
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Lista Ospiti Registrati - Alloggiati Web</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #3B5998; border-bottom: 2px solid #3B5998; padding-bottom: 10px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background-color: #3B5998; color: white; padding: 12px 8px; text-align: left; }
          td { border-bottom: 1px solid #ddd; padding: 10px 8px; }
          tr:hover { background-color: #f5f5f5; }
          .stato-inviata { color: #16a34a; font-weight: bold; }
          .stato-da-inviare { color: #f59e0b; font-weight: bold; }
          .footer { margin-top: 30px; text-align: center; color: #666; font-size: 12px; }
          @media print {
            body { padding: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <h1>Lista Ospiti Registrati</h1>
        <p>Data stampa: ${new Date().toLocaleString('it-IT')}</p>
        <p>Totale ospiti: ${ospitiAttivi.length}</p>
        <table>
          <thead>
            <tr>
              <th>Cognome</th>
              <th>Nome</th>
              <th>Data Nascita</th>
              <th>Arrivo</th>
              <th>Permanenza</th>
              <th>Documento</th>
              <th>Stato</th>
              <th>Prossimo Invio</th>
            </tr>
          </thead>
          <tbody>
            ${ospitiAttivi.map(o => `
              <tr>
                <td>${o.cognome || ''}</td>
                <td>${o.nome || ''}</td>
                <td>${o.dataNascita || ''}</td>
                <td>${o.arrivo || ''}</td>
                <td>${o.permanenza || ''}</td>
                <td>${o.documento || ''} - ${o.numeroDoc || ''}</td>
                <td class="${o.stato === 'INVIATA' ? 'stato-inviata' : 'stato-da-inviare'}">${o.stato || ''}</td>
                <td>${o.data_prossimo_invio ? new Date(o.data_prossimo_invio).toLocaleDateString('it-IT') : '-'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div class="footer">
          <p>Generato da AFFIDYA - Alloggiati Web</p>
        </div>
        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Stampa nominativa degli ospiti inviati in un mese specifico
  const handleStampaListaMese = async (mese) => {
    setLoadingStampaMese(true);
    try {
      const response = await axios.get(`${API_URL}/api/alloggiati-web/ospiti-inviati`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { mese },
      });
      const { ospiti: ospiteMese, totale, invii } = response.data;
      const [anno, meseNum] = mese.split('-');
      const etichettaMese = new Date(parseInt(anno), parseInt(meseNum) - 1, 1)
        .toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error("Popup bloccato - abilita i popup per stampare");
        return;
      }

      const nessunDato = ospiteMese.length === 0;
      const avviso = nessunDato
        ? `<div class="avviso">
             <strong>Nessun dato nominativo disponibile</strong> per questo mese.<br>
             I dati nominativi vengono registrati a partire dagli invii più recenti.
             Il mese selezionato aveva ${invii} invio/i registrati.
           </div>`
        : '';

      const righe = ospiteMese.map(o => `
        <tr>
          <td>${o.cognome || ''}</td>
          <td>${o.nome || ''}</td>
          <td>${o.data_nascita || ''}</td>
          <td>${o.data_arrivo || ''}</td>
          <td>${o.giorni_permanenza || ''}</td>
          <td>${o.tipo_documento || ''} - ${o.numero_documento || ''}</td>
          <td>${o.data_invio || ''}</td>
          <td class="${o.ok === false ? 'esito-errore' : 'esito-ok'}">${o.ok === false ? (o.errore || 'Errore') : 'OK'}</td>
        </tr>
      `).join('');

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Ospiti Inviati - ${etichettaMese}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #3B5998; border-bottom: 2px solid #3B5998; padding-bottom: 10px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { background-color: #3B5998; color: white; padding: 12px 8px; text-align: left; }
            td { border-bottom: 1px solid #ddd; padding: 10px 8px; }
            tr:hover { background-color: #f5f5f5; }
            .esito-ok { color: #16a34a; font-weight: bold; }
            .esito-errore { color: #dc2626; font-weight: bold; }
            .avviso { background: #fef9c3; border: 1px solid #fde047; padding: 12px 16px; border-radius: 6px; margin: 16px 0; }
            .footer { margin-top: 30px; text-align: center; color: #666; font-size: 12px; }
            @media print { body { padding: 0; } .no-print { display: none; } }
          </style>
        </head>
        <body>
          <h1>Ospiti Inviati — ${etichettaMese}</h1>
          <p>Data stampa: ${new Date().toLocaleString('it-IT')}</p>
          <p>Invii nel mese: ${invii} &nbsp;|&nbsp; Totale ospiti: ${totale}</p>
          ${avviso}
          ${!nessunDato ? `
          <table>
            <thead>
              <tr>
                <th>Cognome</th>
                <th>Nome</th>
                <th>Data Nascita</th>
                <th>Arrivo</th>
                <th>Permanenza (gg)</th>
                <th>Documento</th>
                <th>Data Invio</th>
                <th>Esito</th>
              </tr>
            </thead>
            <tbody>${righe}</tbody>
          </table>` : ''}
          <div class="footer"><p>Generato da AFFIDYA - Alloggiati Web</p></div>
          <script>window.onload = function() { window.print(); }<\/script>
        </body>
        </html>
      `);
      printWindow.document.close();
      setShowStampaMeseDialog(false);
    } catch (error) {
      console.error("Errore stampa ospiti mese:", error);
      toast.error("Errore nel caricamento degli ospiti del mese");
    } finally {
      setLoadingStampaMese(false);
    }
  };

  // Funzione per eliminare un ospite
  const handleDeleteOspite = async (ospiteId) => {
    try {
      await axios.delete(`${API_URL}/api/alloggiati-web/ospiti/${ospiteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOspiti(ospiti.filter(o => o._id !== ospiteId));
      toast.success("Ospite eliminato");
    } catch (error) {
      console.error("Errore eliminazione ospite:", error);
      toast.error("Errore nell'eliminazione");
    }
  };

  // Funzione per aprire il modale di modifica data
  const handleEditDate = (ospite) => {
    setEditingOspite(ospite);
    // Converti la data in formato YYYY-MM-DD per l'input date
    let dataForInput = "";
    if (ospite.data_prossimo_invio) {
      dataForInput = new Date(ospite.data_prossimo_invio).toISOString().split('T')[0];
    } else if (ospite.prossimoInvio) {
      // Formato dd/mm/yyyy -> yyyy-mm-dd
      const parts = ospite.prossimoInvio.split('/');
      if (parts.length === 3) {
        dataForInput = `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }
    setNewProssimoInvio(dataForInput);
    setShowEditDateModal(true);
  };

  // Calcola lo stato corretto in base alla data di prossimo invio:
  // se la data è futura l'ospite resta INVIATA (non è ancora il momento di reinviare),
  // se è oggi o passata diventa DA_REINVIARE.
  const statoPerData = (date) => {
    const oggi = new Date();
    oggi.setHours(0, 0, 0, 0);
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d > oggi ? "INVIATA" : "DA_REINVIARE";
  };

  // Funzione per salvare la nuova data
  const handleSaveNewDate = async () => {
    if (!editingOspite || !newProssimoInvio) return;
    
    try {
      const newDate = new Date(newProssimoInvio);
      const nuovoStato = statoPerData(newDate);
      
      await axios.put(`${API_URL}/api/alloggiati-web/ospiti/${editingOspite._id}`, {
        data_prossimo_invio: newDate.toISOString(),
        prossimoInvio: newDate.toLocaleDateString('it-IT'),
        stato: nuovoStato,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Aggiorna lo stato locale
      setOspiti(ospiti.map(o => {
        if (o._id === editingOspite._id) {
          return {
            ...o,
            data_prossimo_invio: newDate.toISOString(),
            prossimoInvio: newDate.toLocaleDateString('it-IT'),
            stato: nuovoStato,
          };
        }
        return o;
      }));
      
      toast.success(
        nuovoStato === "INVIATA"
          ? "Data aggiornata — ospite resta INVIATA fino alla scadenza"
          : "Data aggiornata — ospite in coda per il prossimo invio"
      );
      setShowEditDateModal(false);
      setEditingOspite(null);
    } catch (error) {
      console.error("Errore aggiornamento data:", error);
      toast.error("Errore nell'aggiornamento");
    }
  };

  // Funzione per allineare tutte le date a una data specifica
  const handleAllineaDate = async () => {
    if (!newProssimoInvio) {
      toast.error("Seleziona una data");
      return;
    }
    
    const newDate = new Date(newProssimoInvio);
    const nuovoStato = statoPerData(newDate);
    const ospitiDaAggiornare = ospiti.filter(o => o.stato === "INVIATA");
    
    if (ospitiDaAggiornare.length === 0) {
      toast.info("Nessun ospite da allineare");
      return;
    }
    
    try {
      await Promise.all(ospitiDaAggiornare.map(ospite => 
        axios.put(`${API_URL}/api/alloggiati-web/ospiti/${ospite._id}`, {
          data_prossimo_invio: newDate.toISOString(),
          prossimoInvio: newDate.toLocaleDateString('it-IT'),
          stato: nuovoStato,
        }, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ));
      
      // Aggiorna stato locale
      setOspiti(ospiti.map(o => {
        if (o.stato === "INVIATA") {
          return {
            ...o,
            data_prossimo_invio: newDate.toISOString(),
            prossimoInvio: newDate.toLocaleDateString('it-IT'),
            stato: nuovoStato,
          };
        }
        return o;
      }));
      
      toast.success(
        nuovoStato === "INVIATA"
          ? `Date allineate per ${ospitiDaAggiornare.length} ospiti — resteranno INVIATA fino alla scadenza`
          : `Date allineate per ${ospitiDaAggiornare.length} ospiti — tutti in coda per il prossimo invio`
      );
      setShowEditDateModal(false);
    } catch (error) {
      console.error("Errore allineamento date:", error);
      toast.error("Errore nell'allineamento");
    }
  };

  // Apre il modal "Correggi dati schedina" precompilato con i valori attuali
  // dell'ospite. Serve a recuperare ospiti vecchi (creati prima dei codici
  // Alloggiati Web) o arrivati da prefill fascicolo senza codice.
  const handleOpenFixDati = (ospite, campiEvidenziati = []) => {
    setFixDatiOspite(ospite);
    setFixDatiCampiEvidenziati(Array.isArray(campiEvidenziati) ? campiEvidenziati : []);
    const cn = String(ospite.comuneNascitaCodice || "").trim();
    const citNorm = normalizzaCodiceStatoAlloggiati(
      ospite.cittadinanzaCodice,
      ospite.cittadinanza
    );
    setFixDatiForm({
      luogoRilascio: ospite.luogoRilascio || "",
      luogoRilascioCodice: isCodiceComuneItalianoValido(ospite.luogoRilascioCodice)
        ? ospite.luogoRilascioCodice
        : "",
      comuneNascita: ospite.comuneNascita || "",
      comuneNascitaCodice:
        cn === "000000000" || isCodiceComuneItalianoValido(ospite.comuneNascitaCodice)
          ? cn
          : "",
      provinciaNascita: ospite.provinciaNascita || "",
      statoNascita: ospite.statoNascitaLabel || ospite.statoNascita || "",
      statoNascitaCodice: ospite.statoNascitaCodice || "",
      cittadinanza: etichettaStatoAlloggiati(ospite.cittadinanzaCodice, ospite.cittadinanza),
      cittadinanzaCodice: citNorm,
      dataNascita: dataNascitaPerSchedina(ospite.dataNascita) || ospite.dataNascita || "",
      numeroDoc: ospite.numeroDoc || "",
    });
    setShowFixDatiModal(true);
  };

  const handleCorreggiDaErroreInvio = (item) => {
    if (!item?.ospite) {
      toast.error("Impossibile aprire la correzione: ospite non identificato.");
      return;
    }
    setInvioErrorDialog((s) => ({ ...s, open: false }));
    setActiveTab("ospiti");
    handleOpenFixDati(item.ospite, item.campiCorreggere || []);
  };

  const handleSaveFixDati = async () => {
    if (!fixDatiOspite) return;
    if (!isCodiceComuneItalianoValido(fixDatiForm.luogoRilascioCodice)) {
      toast.error("Seleziona il Luogo di rilascio dall'elenco");
      return;
    }
    const sc = String(fixDatiForm.statoNascitaCodice || "").trim() || CODICE_STATO_ITALIA;
    const ext = sc !== CODICE_STATO_ITALIA;
    if (ext) {
      const cn = String(fixDatiForm.comuneNascitaCodice || "").trim();
      if (!(cn === "000000000" || isCodiceComuneItalianoValido(cn))) {
        toast.error("Nato all'estero: comune deve essere 000000000 oppure seleziona lo stato dall'elenco");
        return;
      }
      if (String(fixDatiForm.provinciaNascita || "").toUpperCase() !== "EE") {
        toast.error("Nato all'estero: la provincia di nascita deve essere EE");
        return;
      }
    } else {
      if (!isCodiceComuneItalianoValido(fixDatiForm.comuneNascitaCodice)) {
        toast.error("Seleziona il Comune di nascita dall'elenco");
        return;
      }
      if (!fixDatiForm.provinciaNascita || fixDatiForm.provinciaNascita.length !== 2) {
        toast.error("Inserisci la provincia di nascita (2 lettere)");
        return;
      }
    }
    const citCodiceFix = normalizzaCodiceStatoAlloggiati(
      fixDatiForm.cittadinanzaCodice,
      fixDatiForm.cittadinanza
    );
    if (!isCodiceIstatStato(citCodiceFix)) {
      toast.error("Seleziona la cittadinanza dall'elenco");
      return;
    }

    setSavingFixDati(true);
    try {
      const updatePayload = {
        luogoRilascio: fixDatiForm.luogoRilascio,
        luogoRilascioCodice: fixDatiForm.luogoRilascioCodice,
        comuneNascita: fixDatiForm.comuneNascita,
        comuneNascitaCodice: fixDatiForm.comuneNascitaCodice,
        provinciaNascita: fixDatiForm.provinciaNascita,
        statoNascita: fixDatiForm.statoNascita,
        statoNascitaCodice: fixDatiForm.statoNascitaCodice,
        cittadinanza: etichettaStatoAlloggiati(citCodiceFix, fixDatiForm.cittadinanza),
        cittadinanzaCodice: citCodiceFix,
        dataNascita: fixDatiForm.dataNascita?.trim() || fixDatiOspite.dataNascita,
        numeroDoc: fixDatiForm.numeroDoc?.trim() || fixDatiOspite.numeroDoc,
      };
      await axios.put(
        `${API_URL}/api/alloggiati-web/ospiti/${fixDatiOspite._id}`,
        updatePayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setOspiti(ospiti.map(o =>
        o._id === fixDatiOspite._id ? { ...o, ...updatePayload } : o
      ));
      toast.success("Dati schedina aggiornati — puoi ripetere test o invio");
      setShowFixDatiModal(false);
      setFixDatiOspite(null);
      setFixDatiCampiEvidenziati([]);
    } catch (error) {
      console.error("Errore aggiornamento dati schedina:", error);
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento");
    } finally {
      setSavingFixDati(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="pl-4 pt-4">
        {/* Page Header - responsive */}
        <PageHeader
          title="Alloggiati Web"
          subtitle="Gestione invio schede alla Questura"
          icon={ShieldCheck}
          iconContainerClassName="bg-gradient-to-r from-[#3B5998] to-[#2d4373]"
          iconClassName="text-white"
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Stats Cards - Compatte */}
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-4 py-2 rounded-full shadow-md" data-testid="stat-ospiti-totali">
            <Users className="w-4 h-4" />
            <span className="text-sm font-medium">Ospiti: <strong>{ospiti.length}</strong></span>
          </div>
          
          <div className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-2 rounded-full shadow-md" data-testid="stat-ospiti-inviati">
            <CheckCircle2 className="w-4 h-4" />
            <span className="text-sm font-medium">Inviate: <strong>{ospitiInviati}</strong></span>
          </div>
          
          <div className="flex items-center gap-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white px-4 py-2 rounded-full shadow-md" data-testid="stat-ricevute">
            <Receipt className="w-4 h-4" />
            <span className="text-sm font-medium">Ricevute: <strong>{ricevute.length}</strong></span>
          </div>
        </div>

        {/* Tabs Navigation */}
        <Tabs value={activeTab} onValueChange={(tab) => { setActiveTab(tab); if (tab === "ospiti") fetchOspiti(); }} className="w-full">
          <TabsList className="grid grid-cols-4 gap-2 bg-white p-1 rounded-xl shadow-sm mb-4 h-auto">
            <TabsTrigger
              value="inserisci"
              className="flex items-center justify-center gap-2 px-3 py-3 rounded-lg font-medium transition-all data-[state=active]:bg-[#3B5998] data-[state=active]:text-white data-[state=active]:shadow-md text-slate-600 hover:bg-slate-100"
              data-testid="tab-inserisci"
            >
              <UserPlus className="w-4 h-4" />
              <span className="hidden sm:inline">Inserisci</span>
            </TabsTrigger>
            <TabsTrigger
              value="ospiti"
              className="flex items-center justify-center gap-2 px-3 py-3 rounded-lg font-medium transition-all data-[state=active]:bg-[#3B5998] data-[state=active]:text-white data-[state=active]:shadow-md text-slate-600 hover:bg-slate-100"
              data-testid="tab-ospiti"
            >
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Ospiti</span>
            </TabsTrigger>
            <TabsTrigger
              value="ricevute"
              className="flex items-center justify-center gap-2 px-3 py-3 rounded-lg font-medium transition-all data-[state=active]:bg-[#3B5998] data-[state=active]:text-white data-[state=active]:shadow-md text-slate-600 hover:bg-slate-100"
              data-testid="tab-ricevute"
            >
              <Receipt className="w-4 h-4" />
              <span className="hidden sm:inline">Ricevute</span>
            </TabsTrigger>
            <TabsTrigger
              value="configura"
              className="flex items-center justify-center gap-2 px-3 py-3 rounded-lg font-medium transition-all data-[state=active]:bg-[#3B5998] data-[state=active]:text-white data-[state=active]:shadow-md text-slate-600 hover:bg-slate-100"
              data-testid="tab-configura"
            >
              <Settings className="w-4 h-4" />
              <span className="hidden sm:inline">Configura</span>
            </TabsTrigger>
          </TabsList>

          {/* Tab Inserisci */}
          <TabsContent value="inserisci">
            <Card className="border-0 shadow-lg rounded-2xl bg-white">
              <CardHeader className="border-b bg-slate-50 rounded-t-2xl">
                <CardTitle className="flex items-center gap-3 text-[#3B5998]">
                  <UserPlus className="w-5 h-5" />
                  Nuova Schedina Alloggiati Web
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {/* Sezione Permanenza */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#3B5998]" />
                    Permanenza
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-xl">
                    <div>
                      <Label className="text-sm text-slate-600">Tipo alloggiato</Label>
                      <Select 
                        value={formData.tipoAlloggiato} 
                        onValueChange={(v) => handleFormChange("tipoAlloggiato", v)}
                      >
                        <SelectTrigger className="mt-1 h-11 rounded-lg border-slate-200" data-testid="select-tipo-alloggiato">
                          <SelectValue placeholder="Seleziona..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="16">16 - Ospite singolo</SelectItem>
                          <SelectItem value="17">17 - Capofamiglia</SelectItem>
                          <SelectItem value="18">18 - Capogruppo</SelectItem>
                          <SelectItem value="19">19 - Familiare</SelectItem>
                          <SelectItem value="20">20 - Membro gruppo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Data di arrivo</Label>
                      <Select 
                        value={formData.dataArrivo} 
                        onValueChange={(v) => handleFormChange("dataArrivo", v)}
                      >
                        <SelectTrigger className="mt-1 h-11 rounded-lg border-slate-200" data-testid="select-data-arrivo">
                          <SelectValue placeholder="Seleziona data..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="oggi">
                            Oggi ({new Date().toLocaleDateString('it-IT')})
                          </SelectItem>
                          <SelectItem value="ieri">
                            Ieri ({new Date(new Date().setDate(new Date().getDate() - 1)).toLocaleDateString('it-IT')})
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Giorni permanenza</Label>
                      <Input
                        type="number"
                        value={formData.giorni}
                        onChange={(e) => handleFormChange("giorni", e.target.value)}
                        className="mt-1 h-11 rounded-lg border-slate-200"
                        data-testid="input-giorni"
                      />
                    </div>
                  </div>
                </div>

                {/* Sezione Anagrafica */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#3B5998]" />
                    Anagrafica
                  </h3>
                  <div className="mb-3 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2.5 text-sm text-sky-950">
                    <div className="flex items-start gap-2">
                      <Info className="w-4 h-4 mt-0.5 shrink-0 text-sky-600" />
                      <div className="space-y-1">
                        <p className="font-medium">Nato all&apos;estero?</p>
                        <p className="text-xs sm:text-sm text-sky-900/90">
                          Seleziona lo <strong>stato di nascita</strong> dall&apos;elenco (es. Svezia): provincia{" "}
                          <strong className="font-mono">EE</strong> e comune{" "}
                          <strong className="font-mono">000000000</strong> si compilano automaticamente.
                          Il <strong>luogo di rilascio</strong> del documento resta un comune italiano dall&apos;autocomplete.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-xl">
                    <div>
                      <Label className="text-sm text-slate-600">Cognome *</Label>
                      <Input
                        value={formData.cognome}
                        onChange={(e) => handleFormChange("cognome", e.target.value)}
                        className="mt-1 h-11 rounded-lg border-slate-200"
                        data-testid="input-cognome"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Nome *</Label>
                      <Input
                        value={formData.nome}
                        onChange={(e) => handleFormChange("nome", e.target.value)}
                        className="mt-1 h-11 rounded-lg border-slate-200"
                        data-testid="input-nome"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Sesso *</Label>
                      <Select 
                        value={formData.sesso} 
                        onValueChange={(v) => handleFormChange("sesso", v)}
                      >
                        <SelectTrigger className="mt-1 h-11 rounded-lg border-slate-200" data-testid="select-sesso">
                          <SelectValue placeholder="Seleziona..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">Maschio</SelectItem>
                          <SelectItem value="2">Femmina</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-sm text-slate-600">Data nascita *</Label>
                      <div className="grid grid-cols-3 gap-2 mt-1">
                        {/* Giorno */}
                        <Select 
                          value={formData.giornoNascita} 
                          onValueChange={(v) => {
                            handleFormChange("giornoNascita", v);
                            if (formData.meseNascita && formData.annoNascita) {
                              handleFormChange("dataNascita", `${formData.annoNascita}-${formData.meseNascita}-${v}`);
                            }
                          }}
                        >
                          <SelectTrigger className="h-11 rounded-lg border-slate-200" data-testid="select-giorno-nascita">
                            <SelectValue placeholder="Giorno" />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({length: 31}, (_, i) => i + 1).map(d => (
                              <SelectItem key={d} value={String(d).padStart(2, '0')}>{d}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {/* Mese */}
                        <Select 
                          value={formData.meseNascita} 
                          onValueChange={(v) => {
                            handleFormChange("meseNascita", v);
                            if (formData.giornoNascita && formData.annoNascita) {
                              handleFormChange("dataNascita", `${formData.annoNascita}-${v}-${formData.giornoNascita}`);
                            }
                          }}
                        >
                          <SelectTrigger className="h-11 rounded-lg border-slate-200" data-testid="select-mese-nascita">
                            <SelectValue placeholder="Mese" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="01">Gennaio</SelectItem>
                            <SelectItem value="02">Febbraio</SelectItem>
                            <SelectItem value="03">Marzo</SelectItem>
                            <SelectItem value="04">Aprile</SelectItem>
                            <SelectItem value="05">Maggio</SelectItem>
                            <SelectItem value="06">Giugno</SelectItem>
                            <SelectItem value="07">Luglio</SelectItem>
                            <SelectItem value="08">Agosto</SelectItem>
                            <SelectItem value="09">Settembre</SelectItem>
                            <SelectItem value="10">Ottobre</SelectItem>
                            <SelectItem value="11">Novembre</SelectItem>
                            <SelectItem value="12">Dicembre</SelectItem>
                          </SelectContent>
                        </Select>
                        {/* Anno */}
                        <Select 
                          value={formData.annoNascita} 
                          onValueChange={(v) => {
                            handleFormChange("annoNascita", v);
                            if (formData.giornoNascita && formData.meseNascita) {
                              handleFormChange("dataNascita", `${v}-${formData.meseNascita}-${formData.giornoNascita}`);
                            }
                          }}
                        >
                          <SelectTrigger className="h-11 rounded-lg border-slate-200" data-testid="select-anno-nascita">
                            <SelectValue placeholder="Anno" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60">
                            {Array.from({length: 107}, (_, i) => 2026 - i).map(y => (
                              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Cittadinanza</Label>
                      <StatoAutocomplete
                        value={formData.cittadinanzaLabel}
                        onChange={(val) => {
                          setFormData((prev) => ({
                            ...prev,
                            cittadinanzaLabel: val,
                            cittadinanza: "",
                          }));
                        }}
                        onSelect={(stato) => {
                          setFormData((prev) => ({
                            ...prev,
                            cittadinanza: stato.codice,
                            cittadinanzaLabel: stato.descrizione,
                          }));
                        }}
                        placeholder="Cerca stato (es. Italia)..."
                        className="mt-1"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Scegli dall&apos;elenco: per l&apos;Italia il codice è{" "}
                        <span className="font-mono">100000100</span>
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Stato di nascita</Label>
                      <StatoAutocomplete
                        value={formData.statoNascitaLabel}
                        onChange={(val) => handleFormChange("statoNascitaLabel", val)}
                        onSelect={(stato) => {
                          handleFormChange("statoNascita", stato.codice);
                          handleFormChange("statoNascitaLabel", stato.descrizione);
                          if (stato.codice !== CODICE_STATO_ITALIA) {
                            handleFormChange("provinciaNascita", "EE");
                            handleFormChange("comuneNascitaCodice", "000000000");
                            handleFormChange("comuneNascita", stato.descrizione);
                          }
                        }}
                        placeholder="Cerca stato (es. Svezia)..."
                        className="mt-1"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Estero → provincia <span className="font-mono">EE</span>, comune{" "}
                        <span className="font-mono">000000000</span>
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Comune di nascita</Label>
                      <ComuneAutocomplete
                        value={formData.comuneNascita}
                        onChange={(val) => handleFormChange("comuneNascita", val)}
                        onSelect={(comune) => {
                          handleFormChange("comuneNascita", comune.descrizione);
                          handleFormChange("provinciaNascita", comune.provincia);
                          handleFormChange("comuneNascitaCodice", comune.codice);
                        }}
                        placeholder={
                          formData.statoNascita && formData.statoNascita !== CODICE_STATO_ITALIA
                            ? "Estero: 000000000 (già impostato)"
                            : "Cerca comune italiano..."
                        }
                        className="mt-1"
                      />
                      {formData.comuneNascitaCodice === "000000000" ? (
                        <p className="text-xs text-emerald-600 mt-1">
                          Codice estero: <span className="font-mono">000000000</span>
                        </p>
                      ) : (
                        <p className="text-xs text-slate-500 mt-1">Italia: scegli il comune dall&apos;elenco</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">
                        Provincia nascita{" "}
                        <span className="font-normal text-slate-400">(RM / EE)</span>
                      </Label>
                      <Input
                        value={formData.provinciaNascita}
                        onChange={(e) => handleFormChange("provinciaNascita", e.target.value.toUpperCase())}
                        placeholder="RM / EE"
                        maxLength={2}
                        className="mt-1 h-11 rounded-lg border-slate-200"
                        data-testid="input-provincia-nascita"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Sigla italiana a 2 lettere, oppure <span className="font-mono font-medium">EE</span> se nato
                        all&apos;estero
                      </p>
                    </div>
                  </div>
                </div>

                {/* Sezione Documento */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#3B5998]" />
                    Documento
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-xl">
                    <div>
                      <Label className="text-sm text-slate-600">Tipo documento *</Label>
                      <Select 
                        value={formData.tipoDocumento} 
                        onValueChange={(v) => handleFormChange("tipoDocumento", v)}
                      >
                        <SelectTrigger className="mt-1 h-11 rounded-lg border-slate-200" data-testid="select-tipo-documento">
                          <SelectValue placeholder="Seleziona..." />
                        </SelectTrigger>
                        <SelectContent className="max-h-80">
                          <SelectItem value="IDENT">Carta di identita</SelectItem>
                          <SelectItem value="IDELE">Carta identita elettronica</SelectItem>
                          <SelectItem value="PASOR">Passaporto ordinario</SelectItem>
                          <SelectItem value="PATEN">Patente di guida</SelectItem>
                          <SelectItem value="PASDI">Passaporto diplomatico</SelectItem>
                          <SelectItem value="PASSE">Passaporto di servizio</SelectItem>
                          <SelectItem value="CIDIP">Carta ID diplomatica</SelectItem>
                          <SelectItem value="CERID">Certificato di identita</SelectItem>
                          <SelectItem value="PATNA">Patente nautica</SelectItem>
                          <SelectItem value="PORM1">Porto fucile uso caccia</SelectItem>
                          <SelectItem value="PORM2">Porto fucile dif. person.</SelectItem>
                          <SelectItem value="PORM3">Porto armi uso sportivo</SelectItem>
                          <SelectItem value="PORM4">Porto pistola dif. person</SelectItem>
                          <SelectItem value="PORM5">Porto armi guardie giur</SelectItem>
                          <SelectItem value="PARLA">Tess. parlamentari</SelectItem>
                          <SelectItem value="MAGIS">Tess. pers. magistrati</SelectItem>
                          <SelectItem value="TESMI">Tess. Ministero Interno</SelectItem>
                          <SelectItem value="TESMG">Tess. Ministero Giustizia</SelectItem>
                          <SelectItem value="TESMF">Tess. Ministero Finanze</SelectItem>
                          <SelectItem value="TESMD">Tess. Ministero Difesa</SelectItem>
                          <SelectItem value="TESMS">Tess. Ministero Sanità</SelectItem>
                          <SelectItem value="TESAE">Tess. Min. Affari Esteri</SelectItem>
                          <SelectItem value="CCUFF">Tess. ufficiale CC</SelectItem>
                          <SelectItem value="CCSOT">Tess. sottufficiali CC</SelectItem>
                          <SelectItem value="CCMIL">Tess. app.to carabinieri</SelectItem>
                          <SelectItem value="PSUFF">Tess. ufficiali P.S.</SelectItem>
                          <SelectItem value="PSSOT">Tess. sovrintendenti P.S.</SelectItem>
                          <SelectItem value="PSAPP">Tess. agenti/ass.ti P.S.</SelectItem>
                          <SelectItem value="GFUFF">Tess. ufficiali G.D.F.</SelectItem>
                          <SelectItem value="GFSOT">Tess. sott.li G.D.F.</SelectItem>
                          <SelectItem value="GFMIL">Tess. app.to finanziere</SelectItem>
                          <SelectItem value="RIFUG">Titolo viaggio rif. polit.</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Numero documento *</Label>
                      <Input
                        value={formData.numeroDocumento}
                        onChange={(e) => handleFormChange("numeroDocumento", e.target.value)}
                        className="mt-1 h-11 rounded-lg border-slate-200"
                        data-testid="input-numero-documento"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Luogo rilascio</Label>
                      <ComuneAutocomplete
                        value={formData.luogoRilascio}
                        onChange={(val) => handleFormChange("luogoRilascio", val)}
                        onSelect={(comune) => {
                          handleFormChange("luogoRilascio", comune.descrizione);
                          handleFormChange("luogoRilascioCodice", comune.codice);
                        }}
                        placeholder="Cerca comune italiano..."
                        className="mt-1"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Sempre un comune italiano dall&apos;elenco (anche con passaporto estero)
                      </p>
                    </div>
                  </div>
                </div>

                {/* Pulsante Inserisci */}
                <div className="flex flex-col items-end gap-2">
                  <Button
                    onClick={handleInserisci}
                    className="h-12 px-8 rounded-xl bg-gradient-to-r from-[#3B5998] to-[#2d4373] hover:from-[#2d4373] hover:to-[#1f2f4f] text-white font-semibold shadow-lg"
                    data-testid="btn-inserisci-invia"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Inserisci ed Invia
                  </Button>
                  <p className="text-xs text-slate-400 italic">
                    Verrà creata in automatico la fascicolo assistenziale relativa all'ospite
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab Ospiti */}
          <TabsContent value="ospiti">
            <Card className="border-0 shadow-lg rounded-2xl bg-white overflow-hidden">
              <CardHeader className="border-b bg-slate-50 flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-3 text-[#3B5998]">
                  <Users className="w-5 h-5" />
                  Lista Ospiti Registrati
                  {ospitiDaReinviare > 0 && (
                    <Badge className="bg-amber-100 text-amber-700 ml-2">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      {ospitiDaReinviare} da reinviare
                    </Badge>
                  )}
                </CardTitle>
                <div className="flex gap-2">
                  {/* Pulsante Aggiorna */}
                  <Button
                    onClick={fetchOspiti}
                    variant="outline"
                    size="sm"
                    disabled={loadingOspiti}
                    className="border-slate-300 text-slate-600 hover:bg-slate-100"
                    title="Aggiorna la lista dal server"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loadingOspiti ? "animate-spin" : ""}`} />
                    Aggiorna
                  </Button>
                  {/* Pulsante Stampa */}
                  <Button
                    onClick={() => setShowStampaMeseDialog(true)}
                    variant="outline"
                    size="sm"
                    className="border-slate-300 text-slate-600 hover:bg-slate-100"
                    data-testid="btn-stampa-lista"
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Stampa Lista
                  </Button>
                  {ospiti.filter((o) => o.stato === "INVIATA").length > 0 && (
                    <Button
                      onClick={() =>
                        handleInvioSchedine(false, { reinvioVerificaIniviati: true })
                      }
                      disabled={sendingSchedine}
                      variant="outline"
                      size="sm"
                      className="border-violet-400 text-violet-700 hover:bg-violet-50"
                      title="Reinvia tutti gli ospiti INVIATA: le date del prossimo invio automatico non cambiano; viene aggiornato ultimo_invio_id se l'invio riesce."
                      data-testid="btn-reinvio-verifica-iniviati"
                    >
                      {sendingSchedine ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      Reinvio verifica (INVIATA)
                    </Button>
                  )}
                  
                  {/* Pulsanti invio (solo se ci sono schedine da inviare) */}
                  {ospiti.filter(o => o.stato === "DA INVIARE" || o.stato === "DA_REINVIARE").length > 0 && (
                    <>
                      <Button
                        onClick={() => handleInvioSchedine(true)}
                        disabled={sendingSchedine}
                        variant="outline"
                        size="sm"
                        className="border-amber-500 text-amber-600 hover:bg-amber-50"
                        data-testid="btn-test-invio"
                      >
                        {sendingSchedine ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <TestTube className="w-4 h-4 mr-2" />
                        )}
                        Test Invio
                      </Button>
                      <Button
                        onClick={() => handleInvioSchedine(false)}
                        disabled={sendingSchedine}
                        size="sm"
                        className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white"
                        data-testid="btn-invia-schedine"
                      >
                        {sendingSchedine ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        Invia alla Questura
                      </Button>
                    </>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {/* Banner warning dati invalidi */}
                {(() => {
                  const ospiti_invalidi = ospiti.filter(
                    (o) => getProblemiSchedinaInvio(o).length > 0
                  );
                  if (ospiti_invalidi.length === 0) return null;

                  // Raggruppa i tipi di problema più frequenti
                  const campiCoinvolti = new Set();
                  ospiti_invalidi.forEach((o) =>
                    getProblemiSchedinaInvio(o).forEach((p) => campiCoinvolti.add(p.campo))
                  );
                  const hasSoloProvincia =
                    campiCoinvolti.size === 1 && campiCoinvolti.has("provinciaNascita");

                  return (
                    <div className="mx-4 mt-4 mb-2 p-4 rounded-xl bg-amber-50 border border-amber-200 flex gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-amber-800 text-sm">
                          {ospiti_invalidi.length} ospite{ospiti_invalidi.length > 1 ? "/i" : ""} con dati incompleti per l&apos;invio automatico
                        </p>
                        <p className="text-amber-700 text-xs mt-1">
                          {hasSoloProvincia
                            ? "La provincia di nascita è mancante o non valida (sigla 2 lettere, es. \"RM\" o \"EE\" per l'estero). Il reinvio automatico invierà spazi al portale e verrà rifiutato."
                            : `Campi con problemi: ${[...campiCoinvolti]
                                .map((c) => ({
                                  comuneNascita: "Comune di nascita",
                                  luogoRilascio: "Luogo rilascio",
                                  provinciaNascita: "Provincia di nascita",
                                  statoNascita: "Stato di nascita (estero)",
                                  cittadinanza: "Cittadinanza",
                                  cognome: "Cognome",
                                  nome: "Nome",
                                  dataNascita: "Data di nascita",
                                  sesso: "Sesso",
                                  documento: "Tipo documento",
                                  numeroDoc: "N° documento",
                                }[c] || c))
                                .join(", ")}.`}
                          {" "}Clicca sull&apos;icona <strong>✏</strong> accanto a ogni ospite per correggere i dati.
                        </p>
                      </div>
                    </div>
                  );
                })()}

                {ospiti.length === 0 ? (
                  <div className="p-8 text-center">
                    <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">Nessun ospite registrato</p>
                    <p className="text-slate-400 text-sm mt-1">Vai alla tab "Inserisci" per aggiungere una schedina</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50 hover:bg-slate-50">
                          <TableHead className="text-slate-700 font-semibold">Ospite</TableHead>
                          <TableHead className="text-slate-700 font-semibold hidden sm:table-cell">Arrivo</TableHead>
                          <TableHead className="text-slate-700 font-semibold hidden md:table-cell">Permanenza</TableHead>
                          <TableHead className="text-slate-700 font-semibold hidden lg:table-cell">Prossimo invio</TableHead>
                          <TableHead className="text-slate-700 font-semibold hidden xl:table-cell">Documento</TableHead>
                          <TableHead className="text-slate-700 font-semibold">Stato</TableHead>
                          <TableHead className="text-slate-700 font-semibold text-right">Azioni</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {ospitiOrdinati.map((ospite) => {
                          const problemiInvio = getProblemiSchedinaInvio(ospite);
                          const haProblemiInvio = problemiInvio.length > 0;
                          const tooltipProblemi = haProblemiInvio
                            ? `Codice mancante o non valido per: ${problemiInvio.map(p => p.label).join(', ')}. Clicca "Correggi dati" per sistemare.`
                            : "";
                          return (
                          <TableRow key={ospite.id} className="hover:bg-slate-50" data-testid={`ospite-row-${ospite.id}`}>
                            <TableCell>
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="font-semibold text-slate-900">{ospite.cognome} {ospite.nome}</p>
                                  {haProblemiInvio && (
                                    <Badge
                                      className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-xs"
                                      title={tooltipProblemi}
                                    >
                                      <AlertTriangle className="w-3 h-3 mr-1" />
                                      Dati incompleti
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-slate-500">Nato il {ospite.dataNascita}</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-slate-600 hidden sm:table-cell">{ospite.arrivo}</TableCell>
                            <TableCell className="text-slate-600 hidden md:table-cell">{ospite.permanenza}</TableCell>
                            <TableCell className="text-slate-600 hidden lg:table-cell">{ospite.prossimoInvio}</TableCell>
                            <TableCell className="hidden xl:table-cell">
                              <div>
                                <p className="text-slate-900 text-sm">{ospite.documento}</p>
                                <p className="text-xs text-slate-500">{ospite.numeroDoc}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              {ospite.stato === "INVIATA" && haProblemiInvio ? (
                                <Badge
                                  className="bg-amber-400 hover:bg-amber-500 text-white font-medium"
                                  title="Schedina inviata in precedenza, ma i dati attuali non sono validi: il prossimo reinvio automatico fallirà. Clicca ✏ per correggere."
                                >
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  INVIATA ⚠
                                </Badge>
                              ) : (
                                <Badge
                                  className={`${ospite.stato === "INVIATA"
                                    ? "bg-emerald-500 hover:bg-emerald-600"
                                    : "bg-amber-500 hover:bg-amber-600"} text-white font-medium`}
                                >
                                  {ospite.stato}
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                {haProblemiInvio && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleOpenFixDati(ospite)}
                                    className="text-amber-600 hover:text-amber-800 hover:bg-amber-50"
                                    title={tooltipProblemi || "Correggi dati per invio Alloggiati Web"}
                                    data-testid={`fix-dati-ospite-${ospite.id}`}
                                  >
                                    <Edit className="w-5 h-5" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleEditDate(ospite)}
                                  className="text-amber-500 hover:text-amber-700 hover:bg-amber-50"
                                  title="Modifica data prossimo invio"
                                  data-testid={`edit-date-ospite-${ospite.id}`}
                                >
                                  <CalendarDays className="w-5 h-5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleShowDetail(ospite)}
                                  className="text-[#3B5998] hover:text-[#2d4373] hover:bg-blue-50"
                                  data-testid={`info-ospite-${ospite.id}`}
                                >
                                  <Info className="w-5 h-5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteOspite(ospite._id)}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`delete-ospite-${ospite.id}`}
                                >
                                  <Trash2 className="w-5 h-5" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab Ricevute */}
          <TabsContent value="ricevute">
            <Card className="border-0 shadow-lg rounded-2xl bg-white">
              <CardHeader className="border-b bg-slate-50">
                <CardTitle className="flex items-center gap-3 text-[#3B5998]">
                  <Receipt className="w-5 h-5" />
                  Storico Ricevute
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6">
                {/* Header con pulsanti */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                  <h4 className="font-semibold text-slate-700">Storico Invii Effettuati</h4>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleForzaDownloadRicevute}
                      disabled={downloadingRicevute}
                      className="text-green-600 border-green-300 hover:bg-green-50"
                      data-testid="btn-scarica-ricevute-pendenti"
                    >
                      {downloadingRicevute ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Scarica ricevute pendenti
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAddRicevutaModal(true)}
                      className="text-purple-600 border-purple-300 hover:bg-purple-50"
                      data-testid="btn-aggiungi-ricevuta"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      + Aggiungi ricevuta
                    </Button>
                  </div>
                </div>
                
                <p className="text-sm text-slate-500 mb-4">
                  Le ricevute vengono scaricate automaticamente il giorno dopo l'invio. Usa "Scarica ricevute pendenti" per forzare il download.
                </p>
                
                {ricevute.length > 0 ? (
                  <div className="space-y-3">
                    {ricevute.map((invio) => (
                      <div 
                        key={invio.id}
                        className={`p-4 rounded-xl border transition-shadow hover:shadow-md ${
                          invio.testMode 
                            ? 'bg-amber-50 border-amber-200' 
                            : 'bg-gradient-to-r from-slate-50 to-slate-100 border-slate-200'
                        }`}
                        data-testid={`invio-${invio.id}`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${invio.testMode ? 'bg-amber-100' : 'bg-purple-100'}`}>
                              <Calendar className={`w-5 h-5 ${invio.testMode ? 'text-amber-600' : 'text-purple-600'}`} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-slate-900 font-semibold">Invio del {invio.data}</p>
                                {invio.testMode && (
                                  <Badge className="bg-amber-500 text-white text-xs">TEST</Badge>
                                )}
                                {invio.manuale && (
                                  <Badge className="bg-blue-500 text-white text-xs">MANUALE</Badge>
                                )}
                                {invio.automatico && (
                                  <Badge className="bg-indigo-500 text-white text-xs">AUTOMATICO</Badge>
                                )}
                                {invio.ricevutaScaricata && (
                                  <Badge className="bg-green-500 text-white text-xs">Ricevuta OK</Badge>
                                )}
                              </div>
                              <p className="text-sm text-slate-500">
                                {invio.schedine} schedine inviate
                                {invio.esito && ` • Esito: ${invio.esito}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {!invio.testMode && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-[#3B5998] border-[#3B5998] hover:bg-[#3B5998]/10 rounded-lg"
                                  data-testid={`download-ricevuta-${invio.id}`}
                                  onClick={() => {
                                    const parts = invio.data.split('/');
                                    if (parts.length === 3) {
                                      const dataISO = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
                                      handleDownloadRicevuta(dataISO, "view");
                                    }
                                  }}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  Visualizza
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-slate-600 border-slate-300 hover:bg-slate-100 rounded-lg"
                                  data-testid={`stampa-ricevuta-${invio.id}`}
                                  onClick={() => {
                                    const parts = invio.data.split('/');
                                    if (parts.length === 3) {
                                      const dataISO = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
                                      handleDownloadRicevuta(dataISO, "print");
                                    }
                                  }}
                                >
                                  <Printer className="w-4 h-4 mr-1" />
                                  Stampa
                                </Button>
                              </>
                            )}
                            {invio.testMode && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-500 border-red-300 hover:bg-red-50 rounded-lg"
                                data-testid={`delete-invio-${invio.id}`}
                                onClick={() => handleDeleteInvioTest(invio.id)}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Elimina
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-slate-50 rounded-xl">
                    <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Nessun invio effettuato ancora</p>
                    <p className="text-sm text-slate-400 mt-1">Gli invii appariranno qui dopo aver inviato le schedine</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab Configura */}
          <TabsContent value="configura">
            <Card className="border-0 shadow-lg rounded-2xl bg-white">
              <CardHeader className="border-b bg-slate-50">
                <CardTitle className="flex items-center gap-3 text-[#3B5998]">
                  <Settings className="w-5 h-5" />
                  Configurazione Accesso
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="max-w-md mx-auto">
                  <div className="text-center mb-6">
                    <div className="inline-flex p-3 bg-[#3B5998]/10 rounded-full mb-4">
                      <ShieldCheck className="w-8 h-8 text-[#3B5998]" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800">Credenziali Servizio</h3>
                    <p className="text-slate-500 text-sm mt-1">Inserisci i dati di accesso per il portale Alloggiati Web della Polizia</p>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm text-slate-600">Utente</Label>
                      <Input
                        value={configData.utente}
                        onChange={(e) => handleConfigChange("utente", e.target.value)}
                        placeholder="Utente del portale Alloggiati Web"
                        className="mt-1 h-11 rounded-lg border-slate-200"
                        autoComplete="off"
                        data-testid="config-utente"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Password</Label>
                      <Input
                        type="password"
                        value={configData.password}
                        onChange={(e) => handleConfigChange("password", e.target.value)}
                        placeholder="Password del portale"
                        className="mt-1 h-11 rounded-lg border-slate-200"
                        autoComplete="new-password"
                        data-testid="config-password"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">WSKEY</Label>
                      <Input
                        value={configData.wskey}
                        onChange={(e) => handleConfigChange("wskey", e.target.value)}
                        placeholder="Chiave Web Service (generata dal portale)"
                        className="mt-1 h-11 rounded-lg font-mono text-sm border-slate-200"
                        autoComplete="off"
                        data-testid="config-wskey"
                      />
                      <details className="mt-2">
                        <summary className="text-xs text-[#3B5998] cursor-pointer hover:underline flex items-center gap-1">
                          <Info className="w-3 h-3" />
                          Come generare la WSKEY?
                        </summary>
                        <div className="mt-2 p-3 bg-slate-50 rounded-lg text-xs text-slate-600 space-y-2">
                          <p className="font-medium text-slate-700">Dal portale Alloggiati Web:</p>
                          <ol className="list-decimal list-inside space-y-1 ml-1">
                            <li>Accedi alla <strong>Pagina Iniziale</strong></li>
                            <li>Clicca sul tuo <strong>nome utente</strong> in alto a destra</li>
                            <li>Seleziona <strong>"Chiave Web Service"</strong></li>
                            <li>Clicca su <strong>"Genera Nuovo Codice"</strong></li>
                          </ol>
                          <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-amber-700">
                            <p className="font-medium">Note importanti:</p>
                            <ul className="list-disc list-inside mt-1">
                              <li>Si può generare <strong>1 codice al giorno</strong></li>
                              <li>Ad ogni <strong>cambio password</strong> va rigenerata</li>
                            </ul>
                          </div>
                        </div>
                      </details>
                    </div>
                    
                    <div className="flex gap-3 mt-6">
                      <Button
                        onClick={handleSaveConfig}
                        className="flex-1 h-12 rounded-xl bg-gradient-to-r from-[#3B5998] to-[#2d4373] hover:from-[#2d4373] hover:to-[#1f2f4f] text-white font-semibold shadow-lg"
                        data-testid="btn-salva-config"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Salva
                      </Button>
                      <Button
                        onClick={handleTestConnection}
                        disabled={testingConnection || !configData.utente}
                        variant="outline"
                        className="flex-1 h-12 rounded-xl border-[#3B5998] text-[#3B5998] hover:bg-[#3B5998]/10 font-semibold"
                        data-testid="btn-test-connessione"
                      >
                        {testingConnection ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Wifi className="w-4 h-4 mr-2" />
                        )}
                        Test Connessione
                      </Button>
                    </div>
                    
                    {/* Status connessione */}
                    {connectionStatus && (
                      <div className={`mt-4 p-4 rounded-xl ${connectionStatus.success ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                        <div className="flex items-center gap-2">
                          {connectionStatus.success ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                          ) : (
                            <WifiOff className="w-5 h-5 text-red-600" />
                          )}
                          <span className={`font-medium ${connectionStatus.success ? 'text-emerald-800' : 'text-red-800'}`}>
                            {connectionStatus.success ? 'Connessione riuscita!' : 'Connessione fallita'}
                          </span>
                        </div>
                        {connectionStatus.message && !connectionStatus.success && (
                          <p className="text-sm text-red-600 mt-2">{connectionStatus.message}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal Dettaglio Ospite */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="w-5 h-5 text-[#3B5998]" />
              Dettagli Ospite
            </DialogTitle>
          </DialogHeader>
          {selectedOspite && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="text-lg font-bold text-slate-900">{selectedOspite.cognome} {selectedOspite.nome}</p>
                <p className="text-sm text-slate-500">Nato il {selectedOspite.dataNascita}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-600 font-medium">Arrivo</p>
                  <p className="text-sm text-slate-900">{selectedOspite.arrivo}</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-xs text-purple-600 font-medium">Permanenza</p>
                  <p className="text-sm text-slate-900">{selectedOspite.permanenza}</p>
                </div>
              </div>
              
              <div className="p-3 bg-amber-50 rounded-lg">
                <p className="text-xs text-amber-600 font-medium">Prossimo Invio</p>
                <p className="text-sm text-slate-900">{selectedOspite.prossimoInvio}</p>
              </div>
              
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-600 font-medium">Documento</p>
                <p className="text-sm text-slate-900">{selectedOspite.documento}</p>
                <p className="text-xs text-slate-500 mt-1">{selectedOspite.numeroDoc}</p>
              </div>
              
              <div className="flex items-center justify-between pt-2">
                <span className="text-sm text-slate-600">Stato:</span>
                <Badge 
                  className={`${selectedOspite.stato === "INVIATA" 
                    ? "bg-emerald-500" 
                    : "bg-amber-500"} text-white font-medium`}
                >
                  {selectedOspite.stato}
                </Badge>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog esito invio (test e invio reale) — per ospite con azione Correggi dati */}
      <Dialog
        open={invioErrorDialog.open}
        onOpenChange={(open) => {
          if (!open) setInvioErrorDialog(INVIO_ERROR_DIALOG_INIT);
          else setInvioErrorDialog((s) => ({ ...s, open: true }));
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-left">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
              {invioErrorDialog.title || "Esito invio"}
            </DialogTitle>
            <DialogDescription asChild>
              <div className="text-left text-slate-700 pt-2 space-y-4">
                <p className="text-sm font-medium text-slate-900 whitespace-pre-line">
                  {invioErrorDialog.summary}
                </p>

                {invioErrorDialog.erroriOspiti?.length > 0 ? (
                  <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Errori per ospite ({invioErrorDialog.erroriOspiti.length})
                    </p>
                    {invioErrorDialog.erroriOspiti.map((item, i) => (
                      <div
                        key={item.ospite?._id || `err-${i}`}
                        className="border border-slate-200 rounded-xl p-4 bg-white shadow-sm space-y-3"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <p className="font-semibold text-slate-900">
                              {nomeOspiteLabel(item.ospite)}
                              {item.indice != null && (
                                <span className="text-slate-500 font-normal text-sm ml-2">
                                  Schedina #{item.indice}
                                </span>
                              )}
                            </p>
                            {item.ospite?.stato && (
                              <p className="text-xs text-slate-500 mt-0.5">
                                Stato: {item.ospite.stato}
                                {item.ospite.arrivo ? ` · Arrivo: ${item.ospite.arrivo}` : ""}
                              </p>
                            )}
                          </div>
                          <Badge
                            variant="outline"
                            className={`text-xs font-medium shrink-0 ${tipoErroreBadgeClass(item.tipo)}`}
                          >
                            {tipoErroreLabel(item.tipo)}
                          </Badge>
                        </div>

                        <div className="space-y-1.5 text-sm">
                          <p>
                            <span className="text-slate-500">Tipo errore: </span>
                            <span className="font-medium text-slate-900">{item.titolo}</span>
                            {item.codice && (
                              <span className="text-slate-500 font-mono text-xs ml-2">
                                ({item.codice})
                              </span>
                            )}
                          </p>
                          <p className="text-slate-800">{item.messaggio}</p>
                          {item.dettaglio && item.dettaglio !== item.messaggio && (
                            <p className="text-slate-600 text-xs">{item.dettaglio}</p>
                          )}
                          {item.suggerimento && (
                            <p className="text-xs text-blue-800 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                              {item.suggerimento}
                            </p>
                          )}
                        </div>

                        {item.puoCorreggere && item.ospite ? (
                          <Button
                            type="button"
                            size="sm"
                            className="bg-amber-500 hover:bg-amber-600 text-white"
                            onClick={() => handleCorreggiDaErroreInvio(item)}
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Correggi dati di {item.ospite.cognome || "questo ospite"}
                          </Button>
                        ) : (
                          <p className="text-xs text-slate-500 italic">
                            {item.tipo === "CONFIGURAZIONE"
                              ? "Apri la scheda Configura per le credenziali."
                              : "Nessuna correzione guidata disponibile per questo errore."}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : invioErrorDialog.dettagli.length > 0 ? (
                  <ul className="list-disc pl-5 space-y-1.5 text-sm text-slate-800 max-h-48 overflow-y-auto">
                    {invioErrorDialog.dettagli.map((riga, idx) => (
                      <li key={idx}>{riga}</li>
                    ))}
                  </ul>
                ) : null}

                {invioErrorDialog.rawResponse && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-slate-500 font-medium">
                      Risposta tecnica (estratto)
                    </summary>
                    <pre className="mt-2 p-3 bg-slate-100 rounded-lg overflow-x-auto max-h-36 text-slate-700 whitespace-pre-wrap break-words">
                      {invioErrorDialog.rawResponse}
                    </pre>
                  </details>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-between gap-2 flex-wrap">
            {invioErrorDialog.erroriOspiti?.find((e) => e.puoCorreggere && e.ospite) && (
              <Button
                type="button"
                variant="outline"
                className="border-amber-300 text-amber-800 hover:bg-amber-50"
                onClick={() => {
                  const first = invioErrorDialog.erroriOspiti.find(
                    (e) => e.puoCorreggere && e.ospite
                  );
                  if (first) handleCorreggiDaErroreInvio(first);
                }}
              >
                <Edit className="w-4 h-4 mr-2" />
                Correggi il primo ospite in errore
              </Button>
            )}
            <Button
              variant="default"
              className="ml-auto"
              onClick={() => setInvioErrorDialog(INVIO_ERROR_DIALOG_INIT)}
            >
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Selezione Mese per Stampa Lista */}
      <Dialog open={showStampaMeseDialog} onOpenChange={setShowStampaMeseDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5 text-slate-600" />
              Stampa Lista Ospiti
            </DialogTitle>
            <DialogDescription>
              Scegli cosa stampare: la lista degli ospiti attualmente attivi oppure la lista nominativa degli ospiti inviati in un mese specifico.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Selezione</label>
              <Select
                value={meseStampa}
                onValueChange={setMeseStampa}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__correnti__">Ospiti attuali (tutti)</SelectItem>
                  {(() => {
                    const mesiSet = new Set();
                    const now = new Date();
                    const chiaveMeseCorrente = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                    mesiSet.add(chiaveMeseCorrente);
                    ricevute.forEach(r => {
                      if (r.dataISO && !r.testMode) {
                        const d = new Date(r.dataISO);
                        if (!isNaN(d)) {
                          mesiSet.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
                        }
                      }
                    });
                    return Array.from(mesiSet)
                      .sort((a, b) => b.localeCompare(a))
                      .map(m => {
                        const [y, mo] = m.split('-');
                        const label = new Date(parseInt(y), parseInt(mo) - 1, 1)
                          .toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
                        return (
                          <SelectItem key={m} value={m}>
                            {label.charAt(0).toUpperCase() + label.slice(1)}
                          </SelectItem>
                        );
                      });
                  })()}
                </SelectContent>
              </Select>
              {meseStampa !== '__correnti__' && (
                <p className="text-xs text-slate-500">
                  I dati nominativi sono disponibili solo per gli invii effettuati dopo l'aggiornamento del sistema.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowStampaMeseDialog(false)}
            >
              Annulla
            </Button>
            <Button
              disabled={loadingStampaMese}
              onClick={() => {
                if (meseStampa === '__correnti__') {
                  setShowStampaMeseDialog(false);
                  handleStampaLista();
                } else {
                  handleStampaListaMese(meseStampa);
                }
              }}
              className="bg-slate-700 hover:bg-slate-800 text-white"
            >
              {loadingStampaMese ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Printer className="w-4 h-4 mr-2" />
              )}
              Stampa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Modifica Data Prossimo Invio */}
      <Dialog open={showEditDateModal} onOpenChange={setShowEditDateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-amber-500" />
              Modifica Data Prossimo Invio
            </DialogTitle>
            <DialogDescription>
              {editingOspite 
                ? `Modifica la data per ${editingOspite.cognome} ${editingOspite.nome}`
                : "Allinea le date di tutti gli ospiti inviati"
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium text-slate-700">Nuova Data Prossimo Invio</Label>
              <Input 
                type="date" 
                value={newProssimoInvio}
                onChange={(e) => setNewProssimoInvio(e.target.value)}
                className="mt-1"
              />
              {newProssimoInvio && (
                <p className="text-xs mt-1.5 text-slate-500">
                  {statoPerData(new Date(newProssimoInvio)) === "INVIATA"
                    ? "📅 Data futura — l'ospite resterà INVIATA e verrà incluso nell'invio solo alla scadenza."
                    : "⚡ Data oggi o passata — l'ospite verrà incluso nel prossimo invio massivo."}
                </p>
              )}
            </div>
            
            {editingOspite && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">
                  Data attuale: <strong>{editingOspite.prossimoInvio || 'Non impostata'}</strong>
                </p>
              </div>
            )}
            
            {/* Opzione per allineare tutte le date */}
            {ospiti.filter(o => o.stato === "INVIATA").length > 1 && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-sm font-medium text-amber-800 mb-2">
                  Allinea tutti gli ospiti
                </p>
                <p className="text-xs text-amber-600 mb-3">
                  Imposta la stessa data per tutti i {ospiti.filter(o => o.stato === "INVIATA").length} ospiti già inviati.
                  Se scegli una data futura restano INVIATA; se scegli oggi o una data passata passano subito in coda di invio.
                </p>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="w-full border-amber-300 text-amber-700 hover:bg-amber-100"
                  onClick={handleAllineaDate}
                >
                  <Users className="w-4 h-4 mr-2" />
                  Allinea Tutte le Date
                </Button>
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowEditDateModal(false)}>
              Annulla
            </Button>
            {editingOspite && (
              <Button 
                onClick={handleSaveNewDate}
                className="bg-amber-500 hover:bg-amber-600 text-white"
              >
                <Save className="w-4 h-4 mr-2" />
                Salva Data
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Correggi dati schedina */}
      <Dialog
        open={showFixDatiModal}
        onOpenChange={(open) => {
          setShowFixDatiModal(open);
          if (!open) {
            setFixDatiCampiEvidenziati([]);
            setFixDatiOspite(null);
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-amber-500" />
              Correggi dati schedina
            </DialogTitle>
            <DialogDescription>
              {fixDatiOspite
                ? `Ospite: ${fixDatiOspite.cognome || ''} ${fixDatiOspite.nome || ''}.`
                : ""}
              {fixDatiCampiEvidenziati.length > 0 && (
                <span className="block mt-2 text-amber-700 font-medium">
                  Campi da correggere evidenziati in giallo.
                </span>
              )}
              {" "}Per l&apos;Italia: comune dall&apos;autocomplete (9 cifre) e provincia (es. RM).
              Per nato all&apos;estero (es. Polonia): seleziona lo <strong>Stato di nascita</strong> dall&apos;elenco — verranno impostati automaticamente provincia <strong>EE</strong> e comune <strong>000000000</strong>.
              La <strong>cittadinanza</strong> va scelta dall&apos;elenco stati (per l&apos;Italia il codice è 100000100, non il testo «ITALIANA»).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2 max-h-[65vh] overflow-y-auto">
            {wrapCampoFix(
              "dataNascita",
              <div>
                <Label className="text-sm font-medium text-slate-700">Data di nascita</Label>
                <Input
                  value={fixDatiForm.dataNascita}
                  onChange={(e) =>
                    setFixDatiForm((f) => ({ ...f, dataNascita: e.target.value }))
                  }
                  placeholder="gg/mm/aaaa"
                  className="mt-1 h-10 rounded-lg border-slate-200"
                />
              </div>
            )}
            {wrapCampoFix(
              "numeroDoc",
              <div>
                <Label className="text-sm font-medium text-slate-700">Numero documento</Label>
                <Input
                  value={fixDatiForm.numeroDoc}
                  onChange={(e) =>
                    setFixDatiForm((f) => ({ ...f, numeroDoc: e.target.value }))
                  }
                  className="mt-1 h-10 rounded-lg border-slate-200 uppercase"
                />
              </div>
            )}
            {wrapCampoFix(
              "statoNascita",
              <div>
              <Label className="text-sm font-medium text-slate-700">
                Stato di nascita *
              </Label>
              <StatoAutocomplete
                value={fixDatiForm.statoNascita}
                onChange={(val) =>
                  setFixDatiForm((f) => ({ ...f, statoNascita: val, statoNascitaCodice: "" }))
                }
                onSelect={(stato) =>
                  setFixDatiForm((f) => {
                    if (stato.codice === CODICE_STATO_ITALIA) {
                      return {
                        ...f,
                        statoNascita: stato.descrizione,
                        statoNascitaCodice: stato.codice,
                        provinciaNascita: "",
                        comuneNascitaCodice: "",
                        comuneNascita: "",
                      };
                    }
                    return {
                      ...f,
                      statoNascita: stato.descrizione,
                      statoNascitaCodice: stato.codice,
                      provinciaNascita: "EE",
                      comuneNascitaCodice: "000000000",
                      comuneNascita: stato.descrizione,
                    };
                  })
                }
                placeholder="Cerca stato (es. Polonia)..."
                className="mt-1"
              />
              <p className="text-xs mt-1 text-slate-600">
                Codice ISTAT:{" "}
                {fixDatiForm.statoNascitaCodice ? (
                  <span className="text-emerald-600 font-mono">{fixDatiForm.statoNascitaCodice}</span>
                ) : (
                  <span className="text-amber-600">seleziona uno stato dall&apos;elenco</span>
                )}
              </p>
            </div>
            )}

            {wrapCampoFix(
              "cittadinanza",
              <div>
              <Label className="text-sm font-medium text-slate-700">
                Cittadinanza *
              </Label>
              <StatoAutocomplete
                value={fixDatiForm.cittadinanza}
                onChange={(val) =>
                  setFixDatiForm((f) => ({ ...f, cittadinanza: val, cittadinanzaCodice: "" }))
                }
                onSelect={(stato) =>
                  setFixDatiForm((f) => ({
                    ...f,
                    cittadinanza: stato.descrizione,
                    cittadinanzaCodice: stato.codice,
                  }))
                }
                placeholder="Cerca cittadinanza (es. Italia)..."
                className="mt-1"
              />
              <p className="text-xs mt-1 text-slate-600">
                Codice ISTAT:{" "}
                {isCodiceIstatStato(
                  normalizzaCodiceStatoAlloggiati(
                    fixDatiForm.cittadinanzaCodice,
                    fixDatiForm.cittadinanza
                  )
                ) ? (
                  <span className="text-emerald-600 font-mono">
                    {normalizzaCodiceStatoAlloggiati(
                      fixDatiForm.cittadinanzaCodice,
                      fixDatiForm.cittadinanza
                    )}
                  </span>
                ) : (
                  <span className="text-amber-600">seleziona uno stato dall&apos;elenco</span>
                )}
              </p>
            </div>
            )}

            {wrapCampoFix(
              "comuneNascita",
              <div>
              <Label className="text-sm font-medium text-slate-700">
                Comune / località di nascita *
              </Label>
              <ComuneAutocomplete
                value={fixDatiForm.comuneNascita}
                onChange={(val) => setFixDatiForm(f => ({
                  ...f,
                  comuneNascita: val,
                  comuneNascitaCodice: "",
                }))}
                onSelect={(comune) => setFixDatiForm(f => ({
                  ...f,
                  comuneNascita: comune.descrizione,
                  comuneNascitaCodice: comune.codice,
                  provinciaNascita: comune.provincia || f.provinciaNascita,
                }))}
                placeholder={
                  String(fixDatiForm.statoNascitaCodice || "").trim() &&
                  fixDatiForm.statoNascitaCodice !== CODICE_STATO_ITALIA
                    ? "Per estero il codice è già 000000000 — puoi lasciare il nome dello stato"
                    : "Cerca comune di nascita (Italia)..."
                }
                className="mt-1"
              />
              <p className="text-xs mt-1">
                {fixDatiForm.comuneNascitaCodice === "000000000" ? (
                  <span className="text-emerald-600">
                    Codice estero valido: 000000000 (provincia EE)
                  </span>
                ) : isCodiceComuneItalianoValido(fixDatiForm.comuneNascitaCodice) ? (
                  <span className="text-emerald-600">Codice: {fixDatiForm.comuneNascitaCodice}</span>
                ) : (
                  <span className="text-amber-600">
                    Codice mancante — per l&apos;Italia scegli il comune; per l&apos;estero scegli prima lo Stato di nascita.
                  </span>
                )}
              </p>
            </div>
            )}

            {wrapCampoFix(
              "provinciaNascita",
              <div>
              <Label className="text-sm font-medium text-slate-700">
                Provincia di nascita *
                <span className="font-normal text-slate-500 ml-1">(RM per Italia; EE obbligatorio se nato all&apos;estero)</span>
              </Label>
              <Input
                value={fixDatiForm.provinciaNascita}
                onChange={(e) =>
                  setFixDatiForm(f => ({
                    ...f,
                    provinciaNascita: e.target.value.toUpperCase().slice(0, 2),
                  }))
                }
                placeholder="RM / EE"
                maxLength={2}
                className="mt-1 h-10 uppercase w-24 rounded-lg border-slate-200"
              />
              {fixDatiForm.provinciaNascita && fixDatiForm.provinciaNascita.length === 2 ? (
                <p className="text-xs mt-1 text-emerald-600">
                  Provincia: {fixDatiForm.provinciaNascita}
                </p>
              ) : (
                <p className="text-xs mt-1 text-amber-600">
                  Due lettere — EE se nato fuori Italia.
                </p>
              )}
            </div>
            )}

            {wrapCampoFix(
              "luogoRilascio",
              <div>
              <Label className="text-sm font-medium text-slate-700">
                Luogo di rilascio documento *
              </Label>
              <ComuneAutocomplete
                value={fixDatiForm.luogoRilascio}
                onChange={(val) => setFixDatiForm(f => ({
                  ...f,
                  luogoRilascio: val,
                  luogoRilascioCodice: "",
                }))}
                onSelect={(comune) => setFixDatiForm(f => ({
                  ...f,
                  luogoRilascio: comune.descrizione,
                  luogoRilascioCodice: comune.codice,
                }))}
                placeholder="Cerca comune (luogo rilascio)..."
                className="mt-1"
              />
              <p className="text-xs mt-1">
                {isCodiceComuneItalianoValido(fixDatiForm.luogoRilascioCodice) ? (
                  <span className="text-emerald-600">
                    Codice: {fixDatiForm.luogoRilascioCodice}
                  </span>
                ) : (
                  <span className="text-amber-600">
                    Codice mancante — scegli una voce dall&apos;elenco.
                  </span>
                )}
              </p>
            </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowFixDatiModal(false);
                setFixDatiCampiEvidenziati([]);
              }}
              disabled={savingFixDati}
            >
              Annulla
            </Button>
            <Button
              onClick={handleSaveFixDati}
              disabled={
                savingFixDati ||
                !isCodiceComuneItalianoValido(fixDatiForm.luogoRilascioCodice) ||
                (() => {
                  if (
                    !isCodiceIstatStato(
                      normalizzaCodiceStatoAlloggiati(
                        fixDatiForm.cittadinanzaCodice,
                        fixDatiForm.cittadinanza
                      )
                    )
                  )
                    return true;
                  const sc =
                    String(fixDatiForm.statoNascitaCodice || "").trim() ||
                    CODICE_STATO_ITALIA;
                  const ext = sc !== CODICE_STATO_ITALIA;
                  if (ext) {
                    const cn = String(fixDatiForm.comuneNascitaCodice || "").trim();
                    if (!(cn === "000000000" || isCodiceComuneItalianoValido(cn)))
                      return true;
                    if (String(fixDatiForm.provinciaNascita || "").toUpperCase() !== "EE")
                      return true;
                    return false;
                  }
                  if (!isCodiceComuneItalianoValido(fixDatiForm.comuneNascitaCodice))
                    return true;
                  if (
                    !fixDatiForm.provinciaNascita ||
                    fixDatiForm.provinciaNascita.length !== 2
                  )
                    return true;
                  return false;
                })()
              }
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {savingFixDati ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Aggiungi Ricevuta Manuale */}
      <Dialog open={showAddRicevutaModal} onOpenChange={setShowAddRicevutaModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Receipt className="w-6 h-6 text-purple-500" />
              Aggiungi Ricevuta
            </DialogTitle>
            <p className="text-sm text-slate-500 mt-1">
              Hai fatto invii dal portale Alloggiati Web? Inserisci la data per scaricare la ricevuta e salvarla nello storico.
            </p>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="data-invio-manuale">Data dell'invio *</Label>
              <Input
                id="data-invio-manuale"
                type="date"
                value={ricevutaManuale.data}
                onChange={(e) => setRicevutaManuale({...ricevutaManuale, data: e.target.value})}
                max={new Date(Date.now() - 86400000).toISOString().split('T')[0]}
                data-testid="input-data-invio-manuale"
              />
              <p className="text-xs text-slate-400">
                La ricevuta è disponibile ~24h dopo l'invio
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="num-schedine-manuale">Numero schedine inviate (opzionale)</Label>
              <Input
                id="num-schedine-manuale"
                type="number"
                min="1"
                value={ricevutaManuale.numSchedine}
                onChange={(e) => setRicevutaManuale({...ricevutaManuale, numSchedine: e.target.value})}
                placeholder="1"
                data-testid="input-num-schedine-manuale"
              />
            </div>

            <div className="p-3 bg-purple-50 rounded-lg text-sm text-purple-700">
              <p>
                <strong>Nota:</strong> Salvando nello storico potrai ritrovare facilmente questa ricevuta in futuro.
              </p>
            </div>
          </div>

          <DialogFooter className="mt-4 flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                if (ricevutaManuale.data) {
                  handleDownloadRicevuta(ricevutaManuale.data, "view");
                } else {
                  toast.error("Inserisci la data dell'invio");
                }
              }}
              disabled={savingRicevuta}
              className="w-full sm:w-auto"
            >
              <Eye className="w-4 h-4 mr-2" />
              Solo Visualizza
            </Button>
            <Button
              onClick={() => handleSaveInvioManuale(true)}
              disabled={savingRicevuta || !ricevutaManuale.data}
              className="bg-purple-600 hover:bg-purple-700 w-full sm:w-auto"
              data-testid="btn-salva-invio-manuale"
            >
              {savingRicevuta ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvataggio...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Salva e Visualizza
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
